import { Fragment, useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { buildUiHelpers, sortBySpec } from './sharedUi';
import { journalEvents, journalPerformance, projectJournalGroup } from './tradeJournalAccounting';

function formatDateValue(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateCompactValue(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function formatMoneyValue(value, currency) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const numeric = Number(value);
  return `${numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ''}`;
}

function formatPercentValue(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const numeric = Number(value);
  return `${numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function formatCompactNumberValue(value, digits = 0) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const numeric = Number(value);
  return numeric.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function formatSyncItemDateTime(dateValue, timeValue) {
  if (!dateValue && !timeValue) return '—';
  let normalizedDateValue = dateValue;
  if (typeof dateValue === 'string' && dateValue.includes('T') && !/[zZ]|[+-]\d{2}:\d{2}$/.test(dateValue)) {
    normalizedDateValue = `${dateValue}Z`;
  }
  const parsedDate = normalizedDateValue ? new Date(normalizedDateValue) : null;
  const hasValidDate = parsedDate && !Number.isNaN(parsedDate.getTime());
  const dateLabel = hasValidDate
    ? parsedDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
    : (dateValue || '');
  const timeLabel = timeValue
    ? String(timeValue).slice(0, 5)
    : (hasValidDate && String(dateValue).includes('T')
      ? parsedDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
      : '');
  return [dateLabel, timeLabel].filter(Boolean).join(' · ') || '—';
}

function parseExternalTradeTime(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  const normalized = text.includes('T') ? text : text.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatExternalTradeClock(value) {
  const parsed = parseExternalTradeTime(value);
  if (!parsed) return '—';
  return parsed.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function groupExternalTrades(items) {
  const groups = new Map();
  for (const item of items || []) {
    const rawTime = String(item?.trade_time || '').trim();
    const symbolRoot = String(item?.symbol || item?.local_symbol || 'UNKNOWN').trim().toUpperCase();
    const key = `${symbolRoot}__${rawTime || 'NO_TIME'}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }

  return Array.from(groups.entries())
    .map(([groupKey, entries]) => {
      const sortedEntries = entries.slice().sort((a, b) => {
        const bagA = String(a?.security_type || '').toUpperCase() === 'BAG' ? 1 : 0;
        const bagB = String(b?.security_type || '').toUpperCase() === 'BAG' ? 1 : 0;
        return bagB - bagA;
      });
      const lead = sortedEntries[0] || {};
      const bagEntry = sortedEntries.find(item => String(item?.security_type || '').toUpperCase() === 'BAG') || null;
      const uniqueActions = Array.from(new Set(sortedEntries.map(item => String(item?.action || '').toUpperCase()).filter(Boolean)));
      const uniqueCurrencies = Array.from(new Set(sortedEntries.map(item => String(item?.currency || '').toUpperCase()).filter(Boolean)));
      const sameCurrency = uniqueCurrencies.length === 1 ? uniqueCurrencies[0] : '';
      const weightedPriceBase = sortedEntries.reduce((sum, item) => sum + (Math.abs(Number(item?.quantity || 0)) * Number(item?.price || 0)), 0);
      const weightedQty = sortedEntries.reduce((sum, item) => sum + Math.abs(Number(item?.quantity || 0)), 0);
      const groupedPrice = bagEntry?.price != null
        ? Number(bagEntry.price)
        : (weightedQty > 0 ? (weightedPriceBase / weightedQty) : null);
      const sizeValue = bagEntry?.quantity != null
        ? Math.abs(Number(bagEntry.quantity || 0))
        : sortedEntries.reduce((maxQty, item) => Math.max(maxQty, Math.abs(Number(item?.quantity || 0))), 0);
      const realizedPnl = sortedEntries.reduce((sum, item) => sum + Number(item?.realized_pnl || 0), 0);
      const hasRealizedPnl = sortedEntries.some(item => item?.realized_pnl != null);
      const commission = sortedEntries.reduce((sum, item) => sum + Math.abs(Number(item?.commission || 0)), 0);
      const hasCommission = sortedEntries.some(item => item?.commission != null);
      const symbolLabel = bagEntry?.local_symbol
        || lead?.local_symbol
        || lead?.symbol
        || 'Unbekannt';
      return {
        key: groupKey,
        trade_time: lead?.trade_time || null,
        symbol: symbolLabel,
        tws_target: String(bagEntry?.tws_target || lead?.tws_target || 'live').trim().toLowerCase() || 'live',
        action: uniqueActions.length === 1 ? uniqueActions[0] : 'MULTI',
        security_type: bagEntry?.security_type || lead?.security_type || null,
        exchange: bagEntry?.exchange || lead?.exchange || null,
        quantity: sizeValue || null,
        price: groupedPrice,
        currency: bagEntry?.currency || lead?.currency || sameCurrency || null,
        realized_pnl: hasRealizedPnl ? realizedPnl : null,
        commission: hasCommission ? commission : null,
        message: lead?.message || null,
        legs: sortedEntries,
      };
    })
    .sort((a, b) => {
      const timeA = parseExternalTradeTime(a?.trade_time)?.getTime() || 0;
      const timeB = parseExternalTradeTime(b?.trade_time)?.getTime() || 0;
      return timeB - timeA;
    });
}

function isPaperTradeJournalGroup(group) {
  if (!group || typeof group !== 'object') return false;
  if (String(group?.tws_target || '').trim().toLowerCase() === 'paper') return true;
  const fills = Array.isArray(group?.fills) ? group.fills : [];
  return fills.some((fill) => String(fill?.tws_target || fill?.source_kind || '').trim().toLowerCase() === 'paper');
}

function isPaperExternalTrade(item) {
  return String(item?.tws_target || '').trim().toLowerCase() === 'paper';
}

function formatHoldTime(seconds) {
  if (!Number.isFinite(seconds)) return '—';
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))} sec`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remainingMinutes = minutes % 60;
    return remainingMinutes ? `${hours}:${String(remainingMinutes).padStart(2, '0')} hr` : `${hours} hr`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function formatOpenTradeAge(openDate) {
  if (!openDate) return '—';
  const start = new Date(openDate);
  if (Number.isNaN(start.getTime())) return '—';
  start.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diffDays = Math.max(0, Math.floor((now.getTime() - start.getTime()) / 86400000));
  return `${diffDays}d`;
}

function toIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function calcPresetRange(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = toIso(today);

  switch (preset) {
    case 'today':
      return { from: todayStr, to: todayStr };
    case 'yesterday': {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      const s = toIso(d);
      return { from: s, to: s };
    }
    case 'last7': {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      return { from: toIso(d), to: todayStr };
    }
    case 'last30': {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      return { from: toIso(d), to: todayStr };
    }
    case 'thisMonth': {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: toIso(d), to: todayStr };
    }
    case 'lastMonth': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: toIso(first), to: toIso(last) };
    }
    case 'last12months': {
      const d = new Date(today);
      d.setFullYear(d.getFullYear() - 1);
      d.setDate(d.getDate() + 1);
      return { from: toIso(d), to: todayStr };
    }
    case 'lastYear': {
      const y = today.getFullYear() - 1;
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    }
    case 'ytd': {
      return { from: `${today.getFullYear()}-01-01`, to: todayStr };
    }
    default:
      return { from: '', to: '' };
  }
}

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'last12months', label: 'Last 12 Months' },
  { key: 'lastYear', label: 'Last Year' },
  { key: 'ytd', label: 'YTD' },
];

const NOTES_LABEL = { Ep: 'Expired', A: 'Assigned', Ex: 'Exercised', P: 'Partial' };
const NOTES_COLOR = { Ep: '#64748b', A: '#f59e0b', Ex: '#3b82f6', P: '#06b6d4' };

const CASH_TXN_TYPES = [
  'FX Conversion',
  'Dividends',
  'Withholding Tax',
  'Payment In Lieu Of Dividends',
  'Broker Interest Paid',
  'Broker Interest Received',
  'Other Fees',
];

function getGroupCategory(group) {
  if (group.call_put === 'SPREAD') return 'SPREAD';
  if (group.call_put === 'STK') return 'STOCK';
  if (group.call_put === 'CASH') return 'FX_CASH';
  if (group.call_put === 'C' || group.call_put === 'P') return 'OPTION';
  return 'OTHER';
}

function matchesSymbolFilter(group, rawFilter, exact = false) {
  const symbol = String(group.symbol || '').toUpperCase();
  const firstToken = symbol.split(/\s+/)[0] || '';
  const filters = String(rawFilter || '')
    .split(/[\s,;\n]+/)
    .map(value => value.trim().toUpperCase())
    .filter(Boolean);
  if (filters.length === 0) return true;

  return filters.some((filter) => {
    if (exact) return symbol === filter || firstToken === filter;
    if (symbol.includes(filter)) return true;
    return firstToken.startsWith(filter) || filter.startsWith(firstToken);
  });
}

function inRange(dateStr, from, to) {
  if (!dateStr) return false;
  const d = dateStr.slice(0, 10);
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

function forexRowWithinRange(row, from, to) {
  if (!from && !to) return true;
  const start = row.period_start ? String(row.period_start).slice(0, 10) : null;
  const end = row.period_end ? String(row.period_end).slice(0, 10) : null;
  if (!start || !end) return false;
  if (from && start < from) return false;
  if (to && end > to) return false;
  return true;
}

function forexRowRank(row) {
  const start = row.period_start ? new Date(row.period_start) : null;
  const end = row.period_end ? new Date(row.period_end) : null;
  const span = (start && end) ? Math.max(0, Math.round((end - start) / 86400000)) : -1;
  const updated = row.updated_at ? new Date(row.updated_at).getTime() : 0;
  return [span, updated, Number(row.import_batch_id || 0), Number(row.id || 0)];
}

function isBetterForexRow(nextRow, currentRow) {
  const nextRank = forexRowRank(nextRow);
  const currentRank = forexRowRank(currentRow);
  for (let i = 0; i < nextRank.length; i += 1) {
    if (nextRank[i] > currentRank[i]) return true;
    if (nextRank[i] < currentRank[i]) return false;
  }
  return false;
}

function formatMonthBucket(value) {
  if (!value) return '—';
  const [year, month] = String(value).split('-');
  const date = new Date(Number(year), Math.max(0, Number(month) - 1), 1);
  return date.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' });
}

function toBaseMoney(value, currency, fxRateToBase, baseCurrency = 'EUR') {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  const numeric = Number(value);
  if (fxRateToBase !== null && fxRateToBase !== undefined && !Number.isNaN(Number(fxRateToBase))) {
    return numeric * Number(fxRateToBase);
  }
  const normalizedCurrency = String(currency || '').trim().toUpperCase();
  const normalizedBase = String(baseCurrency || 'EUR').trim().toUpperCase();
  if (normalizedCurrency && normalizedCurrency === normalizedBase) return numeric;
  return null;
}

function getDetailSignedQuantity(fillDetail) {
  const quantity = Math.abs(Number(fillDetail?.size || 0));
  const action = String(fillDetail?.action || '').trim().toUpperCase();
  if (action === 'SELL' || action === 'SLD') return -quantity;
  if (action === 'BUY' || action === 'BOT') return quantity;
  return Number(fillDetail?.size || 0);
}

function buildManualTradeFillDraft() {
  return {
    symbol: '',
    underlying_symbol: '',
    action: 'BUY',
    instrument_type: 'STK',
    put_call: 'C',
    trade_date: '',
    time: '',
    size: '1',
    price: '',
    currency: 'USD',
    expire: '',
    strike: '',
    commission: '0',
    fees: '0',
    trade_type: 'ExchTrade',
    lifecycle: 'AUTO',
    notes: '',
    description: '',
    multiplier: '',
  };
}

function buildManualOptionLegDraft(overrides = {}) {
  return {
    ...buildManualTradeFillDraft(),
    instrument_type: 'OPTION',
    put_call: 'C',
    ...overrides,
  };
}

function getDetailMultiplier(fillDetail) {
  const rawMultiplier = fillDetail?.multiplier;
  if (rawMultiplier !== null && rawMultiplier !== undefined && rawMultiplier !== '' && !Number.isNaN(Number(rawMultiplier))) {
    return Number(rawMultiplier);
  }
  const instrumentType = String(fillDetail?.instrument_type || '').trim().toUpperCase();
  if (instrumentType === 'C' || instrumentType === 'P') return 100;
  return 1;
}

function deriveOpenOptionLegs(group, baseCurrency = 'EUR') {
  const fills = Array.isArray(group?.fills) ? group.fills : [];
  const legs = new Map();

  for (const fill of fills) {
    const instrumentType = String(fill?.instrument_type || '').trim().toUpperCase();
    if (!['C', 'P'].includes(instrumentType)) continue;
    const signedQty = getDetailSignedQuantity(fill);
    if (!Number.isFinite(signedQty) || Math.abs(signedQty) <= 1e-9) continue;
    const price = Number(fill?.price || 0);
    const strike = fill?.strike != null ? Number(fill.strike) : null;
    const expire = fill?.expire || null;
    const currency = String(fill?.currency || group?.currency || baseCurrency).trim().toUpperCase() || baseCurrency;
    const fx = toBaseMoney(1, currency, fill?.fx_rate_to_base, baseCurrency);
    if (fx == null) return null;
    const multiplier = getDetailMultiplier(fill);
    const commissionBase = Math.abs(Number(toBaseMoney(fill?.commission || 0, currency, fill?.fx_rate_to_base, baseCurrency) || 0))
      + Math.abs(Number(toBaseMoney(fill?.fees || 0, currency, fill?.fx_rate_to_base, baseCurrency) || 0));
    const legKey = `${instrumentType}|${expire || ''}|${strike ?? ''}|${currency}`;
    const entry = legs.get(legKey) || {
      type: instrumentType,
      expiry: expire,
      strike,
      currency,
      fxRate: Number(fx),
      netQty: 0,
      openValueBase: 0,
      openCommissionBase: 0,
      multiplier,
    };
    const currentQty = Number(entry.netQty || 0);

    if (Math.abs(currentQty) <= 1e-9 || Math.sign(currentQty) === Math.sign(signedQty)) {
      entry.netQty = currentQty + signedQty;
      entry.openValueBase += Math.abs(signedQty) * price * multiplier * Number(fx);
      entry.openCommissionBase += commissionBase;
      legs.set(legKey, entry);
      continue;
    }

    const closingQty = Math.min(Math.abs(signedQty), Math.abs(currentQty));
    if (Math.abs(currentQty) > 1e-9) {
      const valuePerUnit = entry.openValueBase / Math.abs(currentQty);
      const commissionPerUnit = entry.openCommissionBase / Math.abs(currentQty);
      entry.openValueBase = Math.max(0, entry.openValueBase - (valuePerUnit * closingQty));
      entry.openCommissionBase = Math.max(0, entry.openCommissionBase - (commissionPerUnit * closingQty));
    }
    const newQty = currentQty + signedQty;
    entry.netQty = newQty;

    if (Math.abs(newQty) > 1e-9 && Math.sign(currentQty) !== Math.sign(newQty)) {
      const residualQty = Math.abs(newQty);
      entry.openValueBase = residualQty * price * multiplier * Number(fx);
      entry.openCommissionBase = commissionBase;
    } else if (Math.abs(newQty) <= 1e-9) {
      entry.openValueBase = 0;
      entry.openCommissionBase = 0;
    }
    legs.set(legKey, entry);
  }

  return Array.from(legs.values()).filter(leg => Math.abs(Number(leg.netQty || 0)) > 1e-9);
}

function calculateOpenOptionRisk(group, baseCurrency = 'EUR') {
  const callPut = String(group?.call_put || '').trim().toUpperCase();
  if (!['C', 'P', 'SPREAD'].includes(callPut)) return null;

  const openLegs = deriveOpenOptionLegs(group, baseCurrency);
  if (!openLegs || openLegs.length === 0) return null;

  const activeLegs = openLegs.filter(leg => Math.abs(Number(leg.netQty || 0)) > 1e-9);
  if (activeLegs.length === 0) return null;

  if (activeLegs.length === 1) {
    const leg = activeLegs[0];
    const qty = Math.abs(Number(leg.netQty || 0));
    const openDebit = Number(leg.openValueBase || 0) + Number(leg.openCommissionBase || 0);
    const openCredit = Math.max(0, Number(leg.openValueBase || 0) - Number(leg.openCommissionBase || 0));
    const strikeBaseValue = leg.strike != null ? Number(leg.strike) * getDetailMultiplier(leg) * qty * Number(leg.fxRate || 1) : null;

    if (leg.netQty > 0 && leg.type === 'C') return { maxWin: 'UNLIMITED', maxLoss: openDebit };
    if (leg.netQty < 0 && leg.type === 'C') return { maxWin: openCredit, maxLoss: 'UNLIMITED' };
    if (leg.netQty > 0 && leg.type === 'P') {
      return {
        maxWin: strikeBaseValue != null ? Math.max(0, strikeBaseValue - openDebit) : null,
        maxLoss: openDebit,
      };
    }
    if (leg.netQty < 0 && leg.type === 'P') {
      return {
        maxWin: openCredit,
        maxLoss: strikeBaseValue != null ? Math.max(0, strikeBaseValue - openCredit) : null,
      };
    }
    return null;
  }

  if (activeLegs.length === 2) {
    const [a, b] = activeLegs;
    const sameType = a.type === b.type;
    const sameExpiry = String(a.expiry || '') === String(b.expiry || '');
    const oppositeDirections = Math.sign(a.netQty) !== Math.sign(b.netQty);
    const equalQty = Math.abs(Math.abs(a.netQty) - Math.abs(b.netQty)) <= 1e-9;
    if (sameType && sameExpiry && oppositeDirections && equalQty && a.strike != null && b.strike != null) {
      const qty = Math.abs(Number(a.netQty || 0));
      const fxRate = Number(a.fxRate || b.fxRate || 1);
      const widthBase = Math.abs(Number(a.strike) - Number(b.strike)) * getDetailMultiplier(a) * qty * fxRate;
      const totalLongValue = activeLegs.filter(leg => leg.netQty > 0).reduce((sum, leg) => sum + Number(leg.openValueBase || 0), 0);
      const totalShortValue = activeLegs.filter(leg => leg.netQty < 0).reduce((sum, leg) => sum + Number(leg.openValueBase || 0), 0);
      const totalCommission = activeLegs.reduce((sum, leg) => sum + Number(leg.openCommissionBase || 0), 0);
      const netCredit = totalShortValue - totalLongValue - totalCommission;
      const netDebit = totalLongValue - totalShortValue + totalCommission;
      if (netCredit > 0) {
        return { maxWin: netCredit, maxLoss: Math.max(0, widthBase - netCredit) };
      }
      return { maxWin: Math.max(0, widthBase - netDebit), maxLoss: Math.max(0, netDebit) };
    }
  }

  return null;
}

function formatRiskValue(value, currency) {
  if (value === 'UNLIMITED') return 'Unbegrenzt';
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return formatMoneyValue(value, currency);
}

function buildOptionProfile(group, baseCurrency = 'EUR', currentUnderlyingPrice = null, currentLivePnlBase = null, mode = 'expiry') {
  const openLegs = deriveOpenOptionLegs(group, baseCurrency);
  if (!openLegs || openLegs.length === 0) return null;

  const expiries = Array.from(new Set(openLegs.map(leg => String(leg.expiry || '')).filter(Boolean)));
  if (expiries.length !== 1) {
    return {
      unsupportedReason: 'Für unterschiedliche Expiries zeigen wir im MVP noch kein gemeinsames Profil.',
      legs: openLegs,
    };
  }

  const strikes = openLegs
    .map(leg => (leg.strike != null ? Number(leg.strike) : null))
    .filter(value => value != null && Number.isFinite(value));
  if (strikes.length === 0) {
    return {
      unsupportedReason: 'Für diesen Trade fehlen die Strike-Daten für das Optionsprofil.',
      legs: openLegs,
    };
  }

  const minStrike = Math.min(...strikes);
  const maxStrike = Math.max(...strikes);
  const reference = currentUnderlyingPrice != null ? Number(currentUnderlyingPrice) : ((minStrike + maxStrike) / 2);
  const anchoredMin = currentUnderlyingPrice != null ? Math.min(minStrike, Number(currentUnderlyingPrice)) : minStrike;
  const anchoredMax = currentUnderlyingPrice != null ? Math.max(maxStrike, Number(currentUnderlyingPrice)) : maxStrike;
  const isSingleLeg = openLegs.length === 1;
  const strikeWidth = Math.max(anchoredMax - anchoredMin, maxStrike - minStrike, Math.abs(reference) * (isSingleLeg ? 0.08 : 0.03), 1);
  const padding = Math.max(strikeWidth * (isSingleLeg ? 1.1 : 0.55), Math.abs(reference) * (isSingleLeg ? 0.08 : 0.04), isSingleLeg ? 12 : 5);
  const pointCount = 81;
  let minPrice = Math.max(0, anchoredMin - padding);
  let maxPrice = anchoredMax + padding;

  const payoffAtPrice = (priceAtExpiry) => openLegs.reduce((sum, leg) => {
    const qty = Math.abs(Number(leg.netQty || 0));
    const multiplier = getDetailMultiplier(leg);
    const fxRate = Number(leg.fxRate || 1);
    const strike = Number(leg.strike || 0);
    const intrinsic = leg.type === 'C'
      ? Math.max(priceAtExpiry - strike, 0)
      : Math.max(strike - priceAtExpiry, 0);
    const intrinsicBase = intrinsic * multiplier * qty * fxRate;
    const longCostBase = Number(leg.openValueBase || 0) + Number(leg.openCommissionBase || 0);
    const shortCreditBase = Number(leg.openValueBase || 0) - Number(leg.openCommissionBase || 0);
    if (leg.netQty > 0) return sum + (intrinsicBase - longCostBase);
    return sum + (shortCreditBase - intrinsicBase);
  }, 0);

  const buildPoints = (rangeMin, rangeMax) => Array.from({ length: pointCount }, (_value, index) => {
    const ratio = pointCount === 1 ? 0 : (index / (pointCount - 1));
    const underlying = rangeMin + ((rangeMax - rangeMin) * ratio);
    return {
      underlying,
      pnl: payoffAtPrice(underlying),
    };
  });

  const hasBreakEvenInPoints = (points) => {
    for (let index = 1; index < points.length; index += 1) {
      const previous = points[index - 1];
      const current = points[index];
      if (Math.abs(previous.pnl) <= 1e-9 || Math.abs(current.pnl) <= 1e-9) return true;
      if ((previous.pnl < 0 && current.pnl > 0) || (previous.pnl > 0 && current.pnl < 0)) return true;
    }
    return false;
  };

  let basePoints = buildPoints(minPrice, maxPrice);
  const allCalls = openLegs.every(leg => leg.type === 'C');
  const allPuts = openLegs.every(leg => leg.type === 'P');
  if (!hasBreakEvenInPoints(basePoints)) {
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const span = Math.max(maxPrice - minPrice, 1);
      if (allPuts) {
        minPrice = Math.max(0, minPrice - (span * (isSingleLeg ? 0.9 : 0.5)));
      } else if (allCalls) {
        maxPrice += span * (isSingleLeg ? 0.9 : 0.5);
      } else {
        minPrice = Math.max(0, minPrice - (span * 0.45));
        maxPrice += span * 0.45;
      }
      basePoints = buildPoints(minPrice, maxPrice);
      if (hasBreakEvenInPoints(basePoints)) break;
    }
  }

  const shiftedPoints = (mode === 'today' && currentUnderlyingPrice != null && currentLivePnlBase != null)
    ? (() => {
        const anchorPnl = payoffAtPrice(Number(currentUnderlyingPrice));
        const offset = Number(currentLivePnlBase) - anchorPnl;
        return basePoints.map(point => ({ ...point, pnl: point.pnl + offset }));
      })()
    : basePoints;

  const breakEvens = [];
  for (let index = 1; index < shiftedPoints.length; index += 1) {
    const previous = shiftedPoints[index - 1];
    const current = shiftedPoints[index];
    if (Math.abs(previous.pnl) <= 1e-9) {
      breakEvens.push(previous.underlying);
      continue;
    }
    if ((previous.pnl < 0 && current.pnl > 0) || (previous.pnl > 0 && current.pnl < 0)) {
      const span = current.pnl - previous.pnl;
      const ratio = Math.abs(span) > 1e-9 ? ((0 - previous.pnl) / span) : 0;
      breakEvens.push(previous.underlying + ((current.underlying - previous.underlying) * ratio));
    }
  }

  const dedupedBreakEvens = breakEvens.reduce((values, item) => {
    if (values.some(existing => Math.abs(existing - item) < 0.01)) return values;
    values.push(item);
    return values;
  }, []);

  const pnls = shiftedPoints.map(point => point.pnl);
  const minPnl = Math.min(...pnls, 0);
  const maxPnl = Math.max(...pnls, 0);

  return {
    expiry: expiries[0],
    points: shiftedPoints,
    minPrice,
    maxPrice,
    minPnl,
    maxPnl,
    breakEvens: dedupedBreakEvens,
    strikes: Array.from(new Set(strikes)).sort((a, b) => a - b),
    currentUnderlyingPrice: currentUnderlyingPrice != null ? Number(currentUnderlyingPrice) : null,
    currentLivePnlBase: currentLivePnlBase != null ? Number(currentLivePnlBase) : null,
    expiryPnlAtCurrentUnderlying: currentUnderlyingPrice != null ? payoffAtPrice(Number(currentUnderlyingPrice)) : null,
    priceCurrency: openLegs[0]?.currency || '',
    mode,
    legs: openLegs,
  };
}

function detectOptionStrategy(group, profile) {
  const legs = Array.isArray(profile?.legs) ? profile.legs : deriveOpenOptionLegs(group);
  if (!legs || legs.length === 0) return null;
  const activeLegs = legs.filter(leg => Math.abs(Number(leg.netQty || 0)) > 1e-9);
  if (activeLegs.length === 0) return null;

  if (activeLegs.length === 1) {
    const leg = activeLegs[0];
    if (leg.type === 'C') return leg.netQty > 0 ? 'Long Call' : 'Short Call';
    if (leg.type === 'P') return leg.netQty > 0 ? 'Long Put' : 'Short Put';
    return null;
  }

  if (activeLegs.length === 2) {
    const [a, b] = [...activeLegs].sort((left, right) => Number(left.strike || 0) - Number(right.strike || 0));
    const sameType = a.type === b.type;
    const sameExpiry = String(a.expiry || '') === String(b.expiry || '');
    const oppositeDirections = Math.sign(a.netQty) !== Math.sign(b.netQty);
    const equalQty = Math.abs(Math.abs(a.netQty) - Math.abs(b.netQty)) <= 1e-9;
    if (sameType && sameExpiry && oppositeDirections && equalQty) {
      if (a.type === 'C') {
        if (a.netQty > 0 && b.netQty < 0) return 'Bull Call Spread';
        if (a.netQty < 0 && b.netQty > 0) return 'Bear Call Spread';
      }
      if (a.type === 'P') {
        if (a.netQty > 0 && b.netQty < 0) return 'Bull Put Spread';
        if (a.netQty < 0 && b.netQty > 0) return 'Bear Put Spread';
      }
    }
  }

  if (activeLegs.length === 4) {
    const calls = activeLegs.filter(leg => leg.type === 'C').sort((left, right) => Number(left.strike || 0) - Number(right.strike || 0));
    const puts = activeLegs.filter(leg => leg.type === 'P').sort((left, right) => Number(left.strike || 0) - Number(right.strike || 0));
    if (calls.length === 2 && puts.length === 2) {
      const isIronCondor = puts[0].netQty > 0 && puts[1].netQty < 0 && calls[0].netQty < 0 && calls[1].netQty > 0;
      if (isIronCondor) return 'Iron Condor';
      const isIronButterfly = Math.abs(Number(puts[1].strike || 0) - Number(calls[0].strike || 0)) <= 1e-9
        && puts[0].netQty > 0 && puts[1].netQty < 0 && calls[0].netQty < 0 && calls[1].netQty > 0;
      if (isIronButterfly) return 'Iron Butterfly';
    }
  }

  return activeLegs.length > 1 ? `${activeLegs.length}-Leg Option` : null;
}

function buildBreakEvenDistance(profile) {
  const currentUnderlying = Number(profile?.currentUnderlyingPrice);
  const breakEvens = Array.isArray(profile?.breakEvens) ? profile.breakEvens.filter(value => Number.isFinite(Number(value))) : [];
  if (!Number.isFinite(currentUnderlying) || breakEvens.length === 0) return null;
  const nearest = breakEvens.reduce((best, value) => (
    best == null || Math.abs(Number(value) - currentUnderlying) < Math.abs(Number(best) - currentUnderlying)
      ? Number(value)
      : best
  ), null);
  if (!Number.isFinite(nearest)) return null;
  const distance = currentUnderlying - nearest;
  const absDistance = Math.abs(distance);
  const percent = Math.abs(nearest) > 1e-9 ? (absDistance / Math.abs(nearest)) * 100 : null;
  return {
    nearest,
    distance,
    absDistance,
    percent,
    direction: distance > 0 ? 'über' : distance < 0 ? 'unter' : 'auf',
  };
}

function interpolateProfilePnl(points, price) {
  if (!Array.isArray(points) || points.length === 0 || !Number.isFinite(Number(price))) return null;
  const target = Number(price);
  if (target <= Number(points[0].underlying)) return Number(points[0].pnl);
  if (target >= Number(points[points.length - 1].underlying)) return Number(points[points.length - 1].pnl);
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    if (target < Number(previous.underlying) || target > Number(current.underlying)) continue;
    const x1 = Number(previous.underlying);
    const x2 = Number(current.underlying);
    const y1 = Number(previous.pnl);
    const y2 = Number(current.pnl);
    const ratio = Math.abs(x2 - x1) > 1e-9 ? (target - x1) / (x2 - x1) : 0;
    return y1 + ((y2 - y1) * ratio);
  }
  return null;
}

function stdNormPdf(x) {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

function stdNormCdf(x) {
  const sign = x < 0 ? -1 : 1;
  const absX = Math.abs(x) / Math.sqrt(2);
  const t = 1 / (1 + 0.3275911 * absX);
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const erf = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX);
  return 0.5 * (1 + sign * erf);
}

function blackScholesPrice(type, s, k, t, r, sigma) {
  if (t <= 0 || sigma <= 0 || s <= 0 || k <= 0) {
    return type === 'C' ? Math.max(s - k, 0) : Math.max(k - s, 0);
  }
  const sqrtT = Math.sqrt(t);
  const d1 = (Math.log(s / k) + (r + 0.5 * sigma * sigma) * t) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;
  if (type === 'C') return s * stdNormCdf(d1) - k * Math.exp(-r * t) * stdNormCdf(d2);
  return k * Math.exp(-r * t) * stdNormCdf(-d2) - s * stdNormCdf(-d1);
}

function impliedVolatility(type, s, k, t, r, marketPrice) {
  if (!Number.isFinite(marketPrice) || marketPrice <= 0 || s <= 0 || k <= 0 || t <= 0) return null;
  let low = 0.0001;
  let high = 5.0;
  for (let i = 0; i < 80; i += 1) {
    const mid = (low + high) / 2;
    const price = blackScholesPrice(type, s, k, t, r, mid);
    if (Math.abs(price - marketPrice) < 1e-5) return mid;
    if (price > marketPrice) high = mid;
    else low = mid;
  }
  return (low + high) / 2;
}

function calculateApproxGreeks(profile, liveRow) {
  const underlying = Number(liveRow?.current_underlying_price);
  const legMarks = Array.isArray(liveRow?.leg_marks) ? liveRow.leg_marks : [];
  if (!Number.isFinite(underlying) || legMarks.length === 0) return null;
  const now = new Date();
  const rate = 0.02;
  const totals = { delta: 0, gamma: 0, theta: 0, vega: 0 };
  let usedLegs = 0;

  legMarks.forEach((leg) => {
    const type = String(leg?.type || '').toUpperCase();
    const strike = Number(leg?.strike);
    const qty = Number(leg?.net_qty);
    const multiplier = Number(leg?.multiplier || 1);
    const mark = Number(leg?.current_mark_price);
    const fx = Number(leg?.fx_rate_to_base || 1);
    const expiryRaw = leg?.expiry;
    if (!['C', 'P'].includes(type) || !Number.isFinite(strike) || !Number.isFinite(qty) || !Number.isFinite(mark)) return;
    const expiry = expiryRaw ? new Date(expiryRaw) : null;
    if (!expiry || Number.isNaN(expiry.getTime())) return;
    const t = Math.max((expiry.getTime() - now.getTime()) / (365.25 * 24 * 3600 * 1000), 1 / 365.25);
    const iv = impliedVolatility(type, underlying, strike, t, rate, mark);
    if (!Number.isFinite(iv) || iv <= 0) return;
    const sqrtT = Math.sqrt(t);
    const d1 = (Math.log(underlying / strike) + (rate + 0.5 * iv * iv) * t) / (iv * sqrtT);
    const d2 = d1 - iv * sqrtT;
    const pdf = stdNormPdf(d1);
    const directionQty = qty * multiplier * fx;
    const delta = type === 'C' ? stdNormCdf(d1) : (stdNormCdf(d1) - 1);
    const gamma = pdf / (underlying * iv * sqrtT);
    const thetaYear = type === 'C'
      ? (-(underlying * pdf * iv) / (2 * sqrtT) - rate * strike * Math.exp(-rate * t) * stdNormCdf(d2))
      : (-(underlying * pdf * iv) / (2 * sqrtT) + rate * strike * Math.exp(-rate * t) * stdNormCdf(-d2));
    const vega = underlying * pdf * sqrtT;
    totals.delta += delta * directionQty;
    totals.gamma += gamma * directionQty;
    totals.theta += (thetaYear / 365) * directionQty;
    totals.vega += (vega / 100) * directionQty;
    usedLegs += 1;
  });

  if (usedLegs === 0) return null;
  return { ...totals, usedLegs };
}

function OptionProfileChart({ profile, currency, COLORS, valueColor, scenarioPrice = null }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const defsId = useMemo(() => `option-profile-${Math.random().toString(36).slice(2, 10)}`, []);
  if (!profile || profile.unsupportedReason) return null;
  const width = 760;
  const height = 240;
  const paddingLeft = 52;
  const paddingRight = 20;
  const paddingTop = 16;
  const paddingBottom = 28;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;
  const minX = profile.minPrice;
  const maxX = profile.maxPrice;
  const minY = Math.min(profile.minPnl, 0);
  const maxY = Math.max(profile.maxPnl, 0);
  const ySpan = Math.max(maxY - minY, 1);
  const xSpan = Math.max(maxX - minX, 1);
  const toX = value => paddingLeft + (((value - minX) / xSpan) * chartWidth);
  const toY = value => paddingTop + (((maxY - value) / ySpan) * chartHeight);
  const zeroY = toY(0);
  const path = profile.points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${toX(point.underlying).toFixed(2)} ${toY(point.pnl).toFixed(2)}`).join(' ');
  const areaPath = `${path} L ${toX(profile.points[profile.points.length - 1].underlying).toFixed(2)} ${zeroY.toFixed(2)} L ${toX(profile.points[0].underlying).toFixed(2)} ${zeroY.toFixed(2)} Z`;
  const currentPoint = profile.currentUnderlyingPrice != null
    ? {
        underlying: profile.currentUnderlyingPrice,
        pnl: profile.points.reduce((closest, point) => (
          Math.abs(point.underlying - profile.currentUnderlyingPrice) < Math.abs(closest.underlying - profile.currentUnderlyingPrice)
            ? point
            : closest
        ), profile.points[0]).pnl,
      }
    : null;
  const scenarioPoint = Number.isFinite(Number(scenarioPrice))
    ? {
        underlying: Number(scenarioPrice),
        pnl: interpolateProfilePnl(profile.points, Number(scenarioPrice)),
      }
    : null;
  const currentLivePnl = profile.currentLivePnlBase != null ? Number(profile.currentLivePnlBase) : (currentPoint ? Number(currentPoint.pnl) : null);
  const expiryPnlAtCurrentUnderlying = profile.expiryPnlAtCurrentUnderlying != null ? Number(profile.expiryPnlAtCurrentUnderlying) : null;
  const showCurrentStateApproximation = profile.mode === 'today' && currentPoint && currentLivePnl != null;
  const currentLiveColor = currentLivePnl != null ? valueColor(currentLivePnl) : "#22c55e";
  const currentLiveStroke = currentLivePnl != null && currentLivePnl < 0 ? "#ef4444cc" : "#22c55ecc";
  const currentLiveBadgeFill = currentLivePnl != null && currentLivePnl < 0 ? "#2a1313" : "#0f2418";
  const currentLiveBadgeStroke = currentLivePnl != null && currentLivePnl < 0 ? "#ef444455" : "#22c55e55";
  const expiryAtCurrentColor = expiryPnlAtCurrentUnderlying != null ? valueColor(expiryPnlAtCurrentUnderlying) : COLORS.muted;
  const hoveredPoint = hoveredIndex != null ? profile.points[Math.max(0, Math.min(profile.points.length - 1, hoveredIndex))] : null;
  const showZeroLabel = Math.abs(maxY) > 1e-9
    && Math.abs(minY) > 1e-9
    && Math.abs(zeroY - toY(maxY)) > 14
    && Math.abs(zeroY - toY(minY)) > 14;

  const handlePointerMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const relativeX = ((event.clientX - bounds.left) / Math.max(bounds.width, 1)) * width;
    const clamped = Math.max(paddingLeft, Math.min(width - paddingRight, relativeX));
    const ratio = (clamped - paddingLeft) / Math.max(chartWidth, 1);
    const index = Math.round(ratio * (profile.points.length - 1));
    setHoveredIndex(index);
  };

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{ width: '100%', height: '240px', display: 'block' }}
      onMouseMove={handlePointerMove}
      onMouseLeave={() => setHoveredIndex(null)}
    >
      <defs>
        <linearGradient id={`${defsId}-positive`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#22c55e66" />
          <stop offset="100%" stopColor="#22c55e0d" />
        </linearGradient>
        <linearGradient id={`${defsId}-negative`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#ef444466" />
          <stop offset="100%" stopColor="#ef44440d" />
        </linearGradient>
        <clipPath id={`${defsId}-positive-clip`}>
          <rect
            x={paddingLeft}
            y={paddingTop}
            width={chartWidth}
            height={Math.max(0, zeroY - paddingTop)}
          />
        </clipPath>
        <clipPath id={`${defsId}-negative-clip`}>
          <rect
            x={paddingLeft}
            y={Math.max(paddingTop, zeroY)}
            width={chartWidth}
            height={Math.max(0, (height - paddingBottom) - zeroY)}
          />
        </clipPath>
      </defs>
      <rect x="0" y="0" width={width} height={height} fill="transparent" />
      <line x1={paddingLeft} y1={zeroY} x2={width - paddingRight} y2={zeroY} stroke="#ffffff22" strokeWidth="1" />
      <line x1={paddingLeft} y1={paddingTop} x2={paddingLeft} y2={height - paddingBottom} stroke="#ffffff18" strokeWidth="1" />
      <path d={areaPath} fill={`url(#${defsId}-positive)`} clipPath={`url(#${defsId}-positive-clip)`} />
      <path d={areaPath} fill={`url(#${defsId}-negative)`} clipPath={`url(#${defsId}-negative-clip)`} />
      {currentPoint && (
        <>
          <line
            x1={toX(currentPoint.underlying)}
            y1={paddingTop}
            x2={toX(currentPoint.underlying)}
            y2={height - paddingBottom}
            stroke="#22c55e88"
            strokeDasharray="6 4"
            strokeWidth="1.5"
          />
          {showCurrentStateApproximation && (
            <line
              x1={paddingLeft}
              y1={toY(currentLivePnl)}
              x2={width - paddingRight}
              y2={toY(currentLivePnl)}
              stroke={currentLiveStroke}
              strokeDasharray="6 4"
              strokeWidth="1.5"
            />
          )}
        </>
      )}
      {scenarioPoint && (
        <>
          <line
            x1={toX(scenarioPoint.underlying)}
            y1={paddingTop}
            x2={toX(scenarioPoint.underlying)}
            y2={height - paddingBottom}
            stroke="#a78bfa88"
            strokeDasharray="4 4"
            strokeWidth="1.25"
          />
          <circle cx={toX(scenarioPoint.underlying)} cy={toY(scenarioPoint.pnl)} r="4" fill="#a78bfa" stroke="#0f1d23" strokeWidth="1.5" />
        </>
      )}
      {profile.strikes.map((strike) => (
        <line
          key={`strike-line-${strike}`}
          x1={toX(strike)}
          y1={paddingTop}
          x2={toX(strike)}
          y2={height - paddingBottom}
          stroke="#22d3ee33"
          strokeDasharray="4 4"
          strokeWidth="1"
        />
      ))}
      {profile.breakEvens.map((value) => (
        <line
          key={`be-line-${value}`}
          x1={toX(value)}
          y1={paddingTop}
          x2={toX(value)}
          y2={height - paddingBottom}
          stroke="#f59e0b66"
          strokeDasharray="2 6"
          strokeWidth="1"
        />
      ))}
      <path d={path} fill="none" stroke="#22d3ee" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {profile.strikes.map((strike, index) => (
        <g key={`strike-badge-${strike}`} transform={`translate(${Math.max(paddingLeft + 6, Math.min(width - paddingRight - 46, toX(strike) - 23))}, ${paddingTop + 8 + (index % 2 === 0 ? 0 : 22)})`}>
          <rect width="46" height="18" rx="9" fill="#0f1d23" stroke="#22d3ee33" />
          <text x="23" y="12.5" textAnchor="middle" fill="#67e8f9" fontSize="10" fontWeight="700">
            {Number(strike).toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </text>
        </g>
      ))}
      {profile.breakEvens.map((value) => (
        <g key={`be-badge-${value}`} transform={`translate(${Math.max(paddingLeft + 6, Math.min(width - paddingRight - 70, toX(value) - 35))}, ${Math.min(height - paddingBottom - 24, zeroY + 36)})`}>
          <rect width="70" height="18" rx="9" fill="#2a1706" stroke="#f59e0b55" />
          <text x="35" y="12.5" textAnchor="middle" fill="#fbbf24" fontSize="10" fontWeight="700">
            BE {Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}
          </text>
        </g>
      ))}
      {currentPoint && (
        <>
          {showCurrentStateApproximation && (
            <g transform={`translate(${Math.max(paddingLeft + 8, width - paddingRight - 112)}, ${Math.max(paddingTop + 4, Math.min(height - paddingBottom - 22, toY(currentLivePnl) - 10))})`}>
              <rect width="104" height="20" rx="10" fill={currentLiveBadgeFill} stroke={currentLiveBadgeStroke} />
              <text x="52" y="13.5" textAnchor="middle" fill={currentLiveColor} fontSize="10.5" fontWeight="700">
                Jetzt {formatMoneyValue(currentLivePnl, currency)}
              </text>
            </g>
          )}
          <g transform={`translate(${Math.max(paddingLeft + 8, Math.min(width - paddingRight - 132, toX(currentPoint.underlying) - 66))}, ${height - paddingBottom - 38})`}>
            <rect width="132" height="36" rx="10" fill="#0f2418" stroke="#22c55e44" />
            <text x="66" y="13" textAnchor="middle" fill="#4ade80" fontSize="10.5" fontWeight="700">
              Kurs {currentPoint.underlying.toLocaleString('en-US', { maximumFractionDigits: 2 })} {profile.priceCurrency || ''}
            </text>
            {expiryPnlAtCurrentUnderlying != null && (
              <text x="66" y="27" textAnchor="middle" fill={expiryAtCurrentColor} fontSize="10.5" fontWeight="700">
                Verfall {formatMoneyValue(expiryPnlAtCurrentUnderlying, currency)}
              </text>
            )}
          </g>
          <circle cx={toX(currentPoint.underlying)} cy={toY(currentPoint.pnl)} r="4" fill="#22c55e" stroke="#0f1d23" strokeWidth="1.5" />
        </>
      )}
      {hoveredPoint && (
        <>
          <line
            x1={toX(hoveredPoint.underlying)}
            y1={paddingTop}
            x2={toX(hoveredPoint.underlying)}
            y2={height - paddingBottom}
            stroke="#ffffff33"
            strokeDasharray="3 5"
            strokeWidth="1"
          />
          <circle cx={toX(hoveredPoint.underlying)} cy={toY(hoveredPoint.pnl)} r="4" fill="#22d3ee" />
          <g transform={`translate(${Math.min(width - 170, toX(hoveredPoint.underlying) + 10)}, ${Math.max(18, toY(hoveredPoint.pnl) - 42)})`}>
            <rect width="156" height="40" rx="8" fill="#09161b" stroke="#ffffff1a" />
            <text x="10" y="16" fill={COLORS.muted} fontSize="11">Underlying {hoveredPoint.underlying.toLocaleString('en-US', { maximumFractionDigits: 2 })} {profile.priceCurrency || ''}</text>
            <text x="10" y="31" fill={valueColor(hoveredPoint.pnl)} fontSize="12" fontWeight="700">P/L {formatMoneyValue(hoveredPoint.pnl, currency)}</text>
          </g>
        </>
      )}
      <text x={paddingLeft - 8} y={toY(maxY)} textAnchor="end" fill={COLORS.muted} fontSize="11">
        {formatMoneyValue(maxY, currency)}
      </text>
      {showZeroLabel && (
        <text x={paddingLeft - 8} y={zeroY + 4} textAnchor="end" fill={COLORS.muted} fontSize="11">
          {formatMoneyValue(0, currency)}
        </text>
      )}
      <text x={paddingLeft - 8} y={toY(minY)} textAnchor="end" fill={COLORS.muted} fontSize="11">
        {formatMoneyValue(minY, currency)}
      </text>
      <text x={paddingLeft} y={height - 8} fill={COLORS.muted} fontSize="11">
        {profile.minPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}
      </text>
      <text x={width - paddingRight} y={height - 8} fill={COLORS.muted} fontSize="11" textAnchor="end">
        {profile.maxPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}
      </text>
    </svg>
  );
}

function buildPnlChartData(groups, mode) {
  const buckets = new Map();
  let skippedGroups = 0;
  const pushValue = (key, label, amount) => {
    if (!buckets.has(key)) buckets.set(key, { key, label, pnl: 0 });
    buckets.get(key).pnl += Number(amount || 0);
  };
  (groups || []).forEach(group => {
    if (journalEvents(group).length && journalPerformance(group).net == null) {
      skippedGroups += 1;
      return;
    }
    journalEvents(group).forEach(event => {
      const amount = event.amount_base;
      if (amount == null) return;
      if (mode === 'setup') {
        const setups = group.setups?.length ? group.setups : [{ id: 'none', name: 'Ohne Setup' }];
        setups.forEach(setup => pushValue(`setup:${setup.id}`, setup.name, amount));
        return;
      }
      const dateValue = String(event.bucket_date || event.date || '').slice(0, 10);
      if (dateValue && mode === 'month') pushValue(dateValue.slice(0, 7), formatMonthBucket(dateValue.slice(0, 7)), amount);
      if (dateValue && mode === 'year') pushValue(dateValue.slice(0, 4), dateValue.slice(0, 4), amount);
    });
  });

  const rows = Array.from(buckets.values());
  rows.sort((a, b) => a.key.localeCompare(b.key));

  let cumulative = 0;
  const series = rows.map((row) => {
    cumulative += row.pnl;
    return { ...row, cumulative };
  });
  return { rows: series, skippedGroups };
}

function PnlComboChart({ data, COLORS, title, subtitle, mode, showCumulativeLine, onToggleCumulativeLine }) {
  if (!data || data.length === 0) {
    return <div style={{ color: COLORS.muted, fontSize: '14px' }}>Keine realisierten Ergebnisse im aktuellen Filter.</div>;
  }

  const values = data.flatMap(item => [item.pnl, item.cumulative, 0]);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);
  const range = Math.max(1, maxValue - minValue);
  const height = 320;
  const axisWidth = 56;
  const leftPad = mode === 'month' ? 56 : 12;
  const rightPad = 18;
  const topPad = 24;
  const bottomPad = 64;
  const bucketWidth = mode === 'month' ? 58 : 84;
  const width = Math.max(mode === 'month' ? 760 : 704, data.length * bucketWidth + leftPad + rightPad);
  const innerWidth = width - leftPad - rightPad;
  const innerHeight = height - topPad - bottomPad;
  const stepX = data.length > 1 ? innerWidth / data.length : innerWidth;
  const barWidth = Math.max(16, Math.min(38, stepX * 0.55));
  const zeroY = topPad + ((maxValue - 0) / range) * innerHeight;
  const ticks = 5;

  const yFor = (value) => topPad + ((maxValue - value) / range) * innerHeight;
  const linePoints = data.map((item, index) => {
    const x = leftPad + stepX * index + stepX / 2;
    const y = yFor(item.cumulative);
    return `${x},${y}`;
  }).join(' ');
  const xLabelEvery = 1;

  const chartSvg = (
    <svg width={width} height={height} role="img">
      {Array.from({ length: ticks + 1 }).map((_, index) => {
        const ratio = index / ticks;
        const y = topPad + innerHeight * ratio;
        return (
          <line key={index} x1={leftPad} y1={y} x2={width - rightPad} y2={y} stroke="#ffffff14" />
        );
      })}
      <line x1={leftPad} y1={zeroY} x2={width - rightPad} y2={zeroY} stroke="#ffffff2a" />
      {data.map((item, index) => {
        const x = leftPad + stepX * index + (stepX - barWidth) / 2;
        const valueY = yFor(item.pnl);
        const y = item.pnl >= 0 ? valueY : zeroY;
        const barHeight = Math.max(2, Math.abs(zeroY - valueY));
        const labelY = item.pnl >= 0 ? y - 8 : y + barHeight + 14;
        return (
          <g key={item.key}>
            <rect
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              rx="4"
              fill={item.pnl >= 0 ? '#4f7cff' : '#d95555'}
            >
              <title>{`${item.label}\nP/L: ${Math.round(item.pnl).toLocaleString('de-DE')}\nKumuliert: ${Math.round(item.cumulative).toLocaleString('de-DE')}`}</title>
            </rect>
            <text x={x + barWidth / 2} y={labelY} textAnchor="middle" fontSize="11" fill={item.pnl >= 0 ? COLORS.text : COLORS.red} fontWeight="700">
              {Math.round(item.pnl).toLocaleString('de-DE')}
            </text>
            {(index % xLabelEvery === 0 || mode === 'setup' || mode === 'month') && (
              <text
                x={x + barWidth / 2}
                y={height - 28}
                textAnchor={mode === 'setup' ? 'middle' : mode === 'month' ? 'middle' : 'end'}
                transform={mode === 'setup' || mode === 'month' ? undefined : `rotate(-35 ${x + barWidth / 2} ${height - 28})`}
                fontSize="11"
                fill={COLORS.muted}
              >
                {item.label}
              </text>
            )}
          </g>
        );
      })}
      {showCumulativeLine && (
        <>
          <polyline fill="none" stroke="#e58a3a" strokeWidth="3" points={linePoints} />
          {data.map((item, index) => {
            const cx = leftPad + stepX * index + stepX / 2;
            const cy = yFor(item.cumulative);
            return (
              <circle key={`${item.key}-point`} cx={cx} cy={cy} r="3.5" fill="#e58a3a">
                <title>{`${item.label}\nKumuliert: ${Math.round(item.cumulative).toLocaleString('de-DE')}`}</title>
              </circle>
            );
          })}
        </>
      )}
    </svg>
  );

  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '18px', fontWeight: 800 }}>{title}</div>
          <div style={{ fontSize: '12px', color: COLORS.muted }}>{subtitle}</div>
        </div>
        <button
          type="button"
          onClick={onToggleCumulativeLine}
          style={{
            background: showCumulativeLine ? `${COLORS.cyan}22` : '#ffffff08',
            border: `1px solid ${showCumulativeLine ? COLORS.cyan : COLORS.border}`,
            borderRadius: '6px',
            color: showCumulativeLine ? COLORS.cyan : COLORS.muted,
            padding: '4px 10px',
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          {showCumulativeLine ? 'Kumulierte Linie aus' : 'Kumulierte Linie an'}
        </button>
      </div>
      {mode === 'month' ? (
        <div style={{ overflowX: 'auto' }}>
          <svg width={width} height={height} role="img">
            {Array.from({ length: ticks + 1 }).map((_, index) => {
              const ratio = index / ticks;
              const value = maxValue - (range * ratio);
              const y = topPad + innerHeight * ratio;
              return (
                <g key={index}>
                  <line x1={leftPad} y1={y} x2={width - rightPad} y2={y} stroke="#ffffff14" />
                  <text x={leftPad - 8} y={y + 4} textAnchor="end" fontSize="11" fill={COLORS.muted}>
                    {Math.round(value).toLocaleString('de-DE')}
                  </text>
                </g>
              );
            })}
            <line x1={leftPad} y1={zeroY} x2={width - rightPad} y2={zeroY} stroke="#ffffff2a" />
            {data.map((item, index) => {
              const x = leftPad + stepX * index + (stepX - barWidth) / 2;
              const valueY = yFor(item.pnl);
              const y = item.pnl >= 0 ? valueY : zeroY;
              const barHeight = Math.max(2, Math.abs(zeroY - valueY));
              const labelY = item.pnl >= 0 ? y - 8 : y + barHeight + 14;
              return (
                <g key={item.key}>
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={barHeight}
                    rx="4"
                    fill={item.pnl >= 0 ? '#4f7cff' : '#d95555'}
                  >
                    <title>{`${item.label}\nP/L: ${Math.round(item.pnl).toLocaleString('de-DE')}\nKumuliert: ${Math.round(item.cumulative).toLocaleString('de-DE')}`}</title>
                  </rect>
                  <text x={x + barWidth / 2} y={labelY} textAnchor="middle" fontSize="11" fill={item.pnl >= 0 ? COLORS.text : COLORS.red} fontWeight="700">
                    {Math.round(item.pnl).toLocaleString('de-DE')}
                  </text>
                  <text x={x + barWidth / 2} y={height - 28} textAnchor="middle" fontSize="11" fill={COLORS.muted}>
                    {item.label}
                  </text>
                </g>
              );
            })}
            {showCumulativeLine && (
              <>
                <polyline fill="none" stroke="#e58a3a" strokeWidth="3" points={linePoints} />
                {data.map((item, index) => {
                  const cx = leftPad + stepX * index + stepX / 2;
                  const cy = yFor(item.cumulative);
                  return (
                    <circle key={`${item.key}-point`} cx={cx} cy={cy} r="3.5" fill="#e58a3a">
                      <title>{`${item.label}\nKumuliert: ${Math.round(item.cumulative).toLocaleString('de-DE')}`}</title>
                    </circle>
                  );
                })}
              </>
            )}
          </svg>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <svg width={axisWidth} height={height} role="presentation" style={{ flex: '0 0 auto' }}>
            {Array.from({ length: ticks + 1 }).map((_, index) => {
              const ratio = index / ticks;
              const value = maxValue - (range * ratio);
              const y = topPad + innerHeight * ratio;
              return (
                <text key={index} x={axisWidth - 8} y={y + 4} textAnchor="end" fontSize="11" fill={COLORS.muted}>
                  {Math.round(value).toLocaleString('de-DE')}
                </text>
              );
            })}
          </svg>
          <div style={{ overflowX: 'auto', flex: 1 }}>
            {chartSvg}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TradeJournalSection({
  COLORS,
  th,
  td,
  tradeJournalSourceFilter,
  setTradeJournalSourceFilter,
  tradeJournalSyncLoading,
  tradeJournalSyncStatus,
  tradeJournalSyncProgress,
  tradeJournalSyncMessage,
  tradeJournalSyncFetchedCount,
  tradeJournalSyncImportedCount,
  tradeJournalSyncSkippedCount,
  tradeJournalSyncImportedItems,
  tradeJournalSyncError,
  tradeJournalLastSyncAt,
  tradeJournalLastSyncImportedCount,
  tradeJournalLastSyncSkippedCount,
  tradeJournalLastSyncItems,
  tradeJournalGroups,
  tradeJournalGroupPage,
  tradeJournalGroupPageLoading,
  tradeJournalOverview,
  tradeJournalOverviewLoading,
  tradeJournalCashSummary,
  tradeJournalCashTransactions,
  tradeJournalCashTransactionsLoaded,
  tradeJournalStatementSummary,
  tradeJournalForexPnl,
  tradeJournalSetups,
  tradeJournalLivePnl,
  tradeJournalLivePnlLoading,
  tradeJournalLivePnlError,
  ladeTradeJournalGroups,
  ladeTradeJournalOverview,
  ladeTradeJournalCashSummary,
  ladeTradeJournalDashboard,
  invalidateTradeJournalDashboard,
  ladeTradeJournalCashTransactions,
  aktualisiereTradeJournalLivePnl,
  starteTradeJournalSync,
  importiereTradeJournalDatei,
  loescheTradeJournalEintraege,
  tradeJournalSetupAnlegen,
  tradeJournalSetupBearbeiten,
  tradeJournalSetupLoeschen,
  tradeJournalGroupSetupsSpeichern,
  tradeJournalBulkGroupSetupsSpeichern,
  tradeJournalGruppeBearbeiten,
  tradeJournalGruppeLoeschen,
  tradeJournalManuelleEintraegeAnlegen,
}) {
  const { Card, Btn } = useMemo(() => buildUiHelpers({ COLORS, th: {} }), [COLORS]);
  const fileInputRef = useRef(null);
  const refreshTradeJournalViewRef = useRef(null);
  const [openGroupIds, setOpenGroupIds] = useState([]);
  const [groupSort, setGroupSort] = useState({ key: 'open_date', direction: 'desc' });
  const [cashTxnOpenTypes, setCashTxnOpenTypes] = useState([]);
  const [tradeListMode, setTradeListMode] = useState('50');
  const [tradePageOffset, setTradePageOffset] = useState(0);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [instrumentFilter, setInstrumentFilter] = useState('ALL');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [debouncedSymbolFilter, setDebouncedSymbolFilter] = useState('');
  const [exactSymbolFilter, setExactSymbolFilter] = useState(false);
  const [setupFilter, setSetupFilter] = useState('ALL');
  const [showPaperTrades, setShowPaperTrades] = useState(false);
  const [setupName, setSetupName] = useState('');
  const [setupDescription, setSetupDescription] = useState('');
  const [editingSetupId, setEditingSetupId] = useState(null);
  const [setupSaving, setSetupSaving] = useState(false);
  const [setupMessage, setSetupMessage] = useState('');
  const [setupError, setSetupError] = useState('');
  const [cashPanelOpen, setCashPanelOpen] = useState(false);
  const [setupAnalyticsPanelOpen, setSetupAnalyticsPanelOpen] = useState(false);
  const [manualTradePanelOpen, setManualTradePanelOpen] = useState(false);
  const [manualTradeRows, setManualTradeRows] = useState([buildManualTradeFillDraft()]);
  const [manualTradeSaving, setManualTradeSaving] = useState(false);
  const [manualTradeError, setManualTradeError] = useState('');
  const [manualTradeMessage, setManualTradeMessage] = useState('');
  const [groupSetupDrafts, setGroupSetupDrafts] = useState({});
  const [groupSetupSavingKey, setGroupSetupSavingKey] = useState('');
  const [openGroupSetupKey, setOpenGroupSetupKey] = useState('');
  const [setupPanelOpen, setSetupPanelOpen] = useState(false);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([]);
  const [bulkSetupIds, setBulkSetupIds] = useState([]);
  const [bulkAssignSaving, setBulkAssignSaving] = useState(false);
  const [editingGroupKey, setEditingGroupKey] = useState('');
  const [groupEditDrafts, setGroupEditDrafts] = useState({});
  const [groupEditSavingKey, setGroupEditSavingKey] = useState('');
  const [groupDeleteSavingKey, setGroupDeleteSavingKey] = useState('');
  const [groupActionMessage, setGroupActionMessage] = useState('');
  const [groupActionError, setGroupActionError] = useState('');
  const [openExternalTradeKeys, setOpenExternalTradeKeys] = useState([]);
  const [optionProfileModes, setOptionProfileModes] = useState({});
  const [optionProfileScenarioPrices, setOptionProfileScenarioPrices] = useState({});
  const [pnlChartMode, setPnlChartMode] = useState('month');
  const [showCumulativeLine, setShowCumulativeLine] = useState(true);
  const [syncLogOpen, setSyncLogOpen] = useState(false);


  // Date filter
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [activePreset, setActivePreset] = useState(null);
  const [dateFilterBasis, setDateFilterBasis] = useState('close');

  const applyPreset = (presetKey) => {
    const range = calcPresetRange(presetKey);
    setFilterFrom(range.from);
    setFilterTo(range.to);
    setActivePreset(presetKey);
  };

  const clearFilter = () => {
    setFilterFrom('');
    setFilterTo('');
    setActivePreset(null);
    setDateFilterBasis('close');
    setStatusFilter('ALL');
    setInstrumentFilter('ALL');
    setSymbolFilter('');
    setExactSymbolFilter(false);
    setSetupFilter('ALL');
    setShowPaperTrades(false);
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSymbolFilter(symbolFilter);
    }, 400);
    return () => window.clearTimeout(timeoutId);
  }, [symbolFilter]);

  const hasFilter = !!(filterFrom || filterTo || statusFilter !== 'ALL' || instrumentFilter !== 'ALL' || symbolFilter.trim() || exactSymbolFilter || setupFilter !== 'ALL' || showPaperTrades);

  const toggleGroup = (groupId) => {
    setOpenGroupIds(ids => ids.includes(groupId) ? ids.filter(id => id !== groupId) : [...ids, groupId]);
  };

  const toggleCashTxnType = (type) => {
    setCashTxnOpenTypes(types => types.includes(type) ? types.filter(t => t !== type) : [...types, type]);
  };

  const toggleGroupSort = (sortKey) => {
    setGroupSort(current => (
      current.key === sortKey
        ? { key: sortKey, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key: sortKey, direction: sortKey === 'open_date' ? 'desc' : 'asc' }
    ));
  };

  const valueColor = (value) => {
    if (value > 0) return COLORS.green;
    if (value < 0) return COLORS.red;
    return COLORS.text;
  };

  const updateManualTradeRow = (index, field, value) => {
    setManualTradeRows(current => current.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      const next = { ...row, [field]: value };
      if (field === 'instrument_type' && String(value).toUpperCase() === 'OPTION') {
        if (!next.underlying_symbol && next.symbol) next.underlying_symbol = next.symbol;
      }
      if (field === 'underlying_symbol' && String(next.instrument_type).toUpperCase() === 'OPTION') {
        next.symbol = value;
      }
      return next;
    }));
  };

  const addManualTradeRow = () => {
    setManualTradeRows(current => [...current, buildManualTradeFillDraft()]);
  };

  const applyManualVerticalSpreadPreset = (putCall = null) => {
    setManualTradeError('');
    setManualTradeMessage('');
    setManualTradeRows((current) => {
      const seed = current[0] || buildManualTradeFillDraft();
      const normalizedPutCall = String(putCall || seed.put_call || 'C').toUpperCase() === 'P' ? 'P' : 'C';
      const common = {
        symbol: seed.symbol || '',
        underlying_symbol: seed.underlying_symbol || seed.symbol || '',
        trade_date: seed.trade_date || '',
        time: seed.time || '',
        currency: seed.currency || 'USD',
        expire: seed.expire || '',
        lifecycle: seed.lifecycle || 'AUTO',
        multiplier: seed.multiplier || '',
        trade_type: seed.trade_type || 'ExchTrade',
        notes: '',
        description: '',
      };
      return [
        buildManualOptionLegDraft({
          ...common,
          action: 'SELL',
          put_call: normalizedPutCall,
          size: seed.size || '1',
          price: '',
          strike: '',
          commission: '0',
          fees: '0',
        }),
        buildManualOptionLegDraft({
          ...common,
          action: 'BUY',
          put_call: normalizedPutCall,
          size: seed.size || '1',
          price: '',
          strike: '',
          commission: '0',
          fees: '0',
        }),
      ];
    });
  };

  const removeManualTradeRow = (index) => {
    setManualTradeRows(current => (
      current.length <= 1 ? [buildManualTradeFillDraft()] : current.filter((_, rowIndex) => rowIndex !== index)
    ));
  };

  const saveManualTrades = async () => {
    setManualTradeSaving(true);
    setManualTradeError('');
    setManualTradeMessage('');
    try {
      const payload = manualTradeRows.map((row) => ({
        symbol: row.instrument_type === 'OPTION'
          ? (row.underlying_symbol || row.symbol)
          : row.symbol,
        underlying_symbol: row.underlying_symbol || null,
        action: row.action,
        instrument_type: row.instrument_type,
        put_call: row.instrument_type === 'OPTION' ? row.put_call : null,
        trade_date: row.trade_date,
        time: row.time || null,
        size: Number(row.size),
        price: Number(row.price),
        currency: row.currency,
        expire: row.expire || null,
        strike: row.strike === '' ? null : Number(row.strike),
        commission: row.commission === '' ? 0 : Number(row.commission),
        fees: 0,
        fx_rate_to_base: String(row.currency || '').trim().toUpperCase() === 'EUR' ? 1 : null,
        trade_type: row.trade_type || 'ExchTrade',
        open_close_indicator: row.lifecycle === 'OPEN' ? 'O' : row.lifecycle === 'CLOSE' ? 'C' : null,
        notes: row.notes || null,
        description: row.description || null,
        multiplier: row.multiplier === '' ? null : Number(row.multiplier),
      }));
      const result = await tradeJournalManuelleEintraegeAnlegen(payload);
      await refreshTradeJournalView();
      setManualTradeRows([buildManualTradeFillDraft()]);
      setManualTradeMessage(`${result?.affected_fills || payload.length} Fill-Zeilen manuell gespeichert.`);
    } catch (err) {
      setManualTradeError(err?.message || 'Manuelle Eingabe konnte nicht gespeichert werden');
    } finally {
      setManualTradeSaving(false);
    }
  };

  const resetSetupForm = () => {
    setSetupName('');
    setSetupDescription('');
    setEditingSetupId(null);
    setSetupError('');
    setSetupMessage('');
  };

  const startSetupEdit = (setup) => {
    setSetupPanelOpen(true);
    setEditingSetupId(setup.id);
    setSetupName(setup.name || '');
    setSetupDescription(setup.description || '');
    setSetupError('');
    setSetupMessage('');
  };

  const getAssignedSetupIds = (group) => {
    const draft = groupSetupDrafts[group.group_key];
    if (Array.isArray(draft)) return draft;
    return Array.isArray(group.setups) ? group.setups.map(setup => setup.id) : [];
  };

  const toggleGroupSetupDraft = (groupKey, setupId) => {
    setGroupSetupDrafts(current => {
      const active = new Set(Array.isArray(current[groupKey]) ? current[groupKey] : []);
      if (active.has(setupId)) active.delete(setupId);
      else active.add(setupId);
      return { ...current, [groupKey]: Array.from(active).sort((a, b) => a - b) };
    });
  };

  const toggleGroupSelection = (groupKey) => {
    setSelectedGroupKeys(current => (
      current.includes(groupKey)
        ? current.filter(key => key !== groupKey)
        : [...current, groupKey]
    ));
  };

  const toggleSelectAllVisibleGroups = () => {
    const visibleKeys = visibleGroups.map(group => group.group_key).filter(Boolean);
    const allSelected = visibleKeys.length > 0 && visibleKeys.every(key => selectedGroupKeys.includes(key));
    setSelectedGroupKeys(current => {
      if (allSelected) {
        return current.filter(key => !visibleKeys.includes(key));
      }
      const next = new Set(current);
      visibleKeys.forEach(key => next.add(key));
      return Array.from(next);
    });
  };

  const toggleBulkSetupId = (setupId) => {
    setBulkSetupIds(current => (
      current.includes(setupId)
        ? current.filter(id => id !== setupId)
        : [...current, setupId].sort((a, b) => a - b)
    ));
  };

  const buildGroupEditDraft = (group) => ({
    fills: (group.fills || []).map((fill) => ({
      id: fill.id,
      action: fill.action || '',
      instrument_type: fill.instrument_type || '',
      expire: fill.expire || '',
      strike: fill.strike ?? '',
      trade_date: fill.trade_date || '',
      time: fill.time || '',
      size: fill.size ?? '',
      position: fill.position ?? null,
      price: fill.price ?? '',
      return_amount: fill.return_amount ?? null,
      commission: fill.commission ?? '',
      fees: fill.fees ?? '',
      currency: fill.currency || '',
      trade_type: fill.trade_type || '',
      notes: fill.notes || '',
      fx_rate_to_base: fill.fx_rate_to_base ?? '',
      open_close_indicator: fill.open_close_indicator || '',
    })),
  });

  const startGroupEdit = (group) => {
    setEditingGroupKey(group.group_key);
    setGroupEditDrafts(current => ({ ...current, [group.group_key]: buildGroupEditDraft(group) }));
    setGroupActionError('');
    setGroupActionMessage('');
  };

  const cancelGroupEdit = (groupKey) => {
    setEditingGroupKey(current => (current === groupKey ? '' : current));
    setGroupEditDrafts(current => {
      const next = { ...current };
      delete next[groupKey];
      return next;
    });
  };

  const updateGroupEditField = (groupKey, fillId, field, value) => {
    setGroupEditDrafts(current => {
      const draft = current[groupKey] || { fills: [] };
      return {
        ...current,
        [groupKey]: {
          ...draft,
          fills: draft.fills.map(fill => (
            fill.id === fillId ? { ...fill, [field]: value } : fill
          )),
        },
      };
    });
  };

  const submitSetupForm = async (event) => {
    event.preventDefault();
    setSetupSaving(true);
    setSetupError('');
    setSetupMessage('');
    try {
      const payload = { name: setupName, description: setupDescription };
      const saved = editingSetupId
        ? await tradeJournalSetupBearbeiten(editingSetupId, payload)
        : await tradeJournalSetupAnlegen(payload);
      await refreshTradeJournalView();
      resetSetupForm();
      setSetupMessage(editingSetupId ? `Setup "${saved.name}" aktualisiert.` : `Setup "${saved.name}" erstellt.`);
    } catch (err) {
      setSetupError(err?.message || 'Setup konnte nicht gespeichert werden');
    } finally {
      setSetupSaving(false);
    }
  };

  const removeSetup = async (setup) => {
    if (!window.confirm(`Setup "${setup.name}" wirklich löschen?`)) return;
    setSetupSaving(true);
    setSetupError('');
    setSetupMessage('');
    try {
      await tradeJournalSetupLoeschen(setup.id);
      await refreshTradeJournalView();
      if (editingSetupId === setup.id) resetSetupForm();
      setSetupMessage(`Setup "${setup.name}" gelöscht.`);
    } catch (err) {
      setSetupError(err?.message || 'Setup konnte nicht gelöscht werden');
    } finally {
      setSetupSaving(false);
    }
  };

  const saveGroupSetups = async (group) => {
    const selectedIds = getAssignedSetupIds(group);
    setGroupSetupSavingKey(group.group_key);
    setSetupError('');
    setSetupMessage('');
    try {
      await tradeJournalGroupSetupsSpeichern(group.group_key, selectedIds);
      setGroupSetupDrafts(current => {
        const next = { ...current };
        delete next[group.group_key];
        return next;
      });
      setOpenGroupSetupKey(current => current === group.group_key ? '' : current);
      setSetupMessage(`Setups für ${group.symbol || 'Trade'} gespeichert.`);
      try {
        await refreshTradeJournalViewRef.current?.();
      } catch (err) {
        setSetupError('Setups wurden gespeichert. Die Ansicht konnte nicht aktualisiert werden. Bitte neu laden.');
      }
    } catch (err) {
      setSetupError(err?.message || 'Setups konnten nicht gespeichert werden');
    } finally {
      setGroupSetupSavingKey('');
    }
  };

  const saveBulkSetups = async () => {
    if (selectedGroupKeys.length === 0) return;
    setBulkAssignSaving(true);
    setSetupError('');
    setSetupMessage('');
    try {
      await tradeJournalBulkGroupSetupsSpeichern(selectedGroupKeys, bulkSetupIds);
      setSelectedGroupKeys([]);
      setSetupMessage(`Setups für ${selectedGroupKeys.length} Trades gespeichert.`);
      try {
        await refreshTradeJournalViewRef.current?.();
      } catch (err) {
        setSetupError('Setups wurden gespeichert. Die Ansicht konnte nicht aktualisiert werden. Bitte neu laden.');
      }
    } catch (err) {
      setSetupError(err?.message || 'Setups konnten nicht gesammelt gespeichert werden');
    } finally {
      setBulkAssignSaving(false);
    }
  };

  const saveGroupEdit = async (group) => {
    const draft = groupEditDrafts[group.group_key];
    if (!draft || !Array.isArray(draft.fills) || draft.fills.length === 0) return;
    setGroupEditSavingKey(group.group_key);
    setGroupActionError('');
    setGroupActionMessage('');
    try {
      const payload = draft.fills.map(fill => ({
        id: fill.id,
        action: fill.action || null,
        instrument_type: fill.instrument_type || null,
        expire: fill.expire || null,
        strike: fill.strike === '' ? null : Number(fill.strike),
        trade_date: fill.trade_date || null,
        time: fill.time || null,
        size: fill.size === '' ? null : Number(fill.size),
        price: fill.price === '' ? null : Number(fill.price),
        commission: fill.commission === '' ? null : Number(fill.commission),
        fees: fill.fees === '' ? null : Number(fill.fees),
        currency: fill.currency || null,
        trade_type: fill.trade_type || null,
        notes: fill.notes || null,
        fx_rate_to_base: fill.fx_rate_to_base === '' ? null : Number(fill.fx_rate_to_base),
        open_close_indicator: fill.open_close_indicator || null,
      }));
      const result = await tradeJournalGruppeBearbeiten(group.group_key, payload);
      cancelGroupEdit(group.group_key);
      setGroupActionMessage(`Trade ${group.symbol || group.group_key} gespeichert.${result?.updated_group_key && result.updated_group_key !== group.group_key ? ' Gruppierung wurde aktualisiert.' : ''}`);
      try {
        await refreshTradeJournalView();
      } catch (err) {
        setGroupActionError('Trade gespeichert. Die Ansicht konnte nicht aktualisiert werden. Bitte neu laden.');
      }
    } catch (err) {
      setGroupActionError(err?.message || 'Trade konnte nicht gespeichert werden');
    } finally {
      setGroupEditSavingKey('');
    }
  };

  const deleteGroup = async (group) => {
    if (!window.confirm(`Trade "${group.symbol || group.group_key}" wirklich löschen? Es werden alle zugehörigen Fills entfernt.`)) return;
    setGroupDeleteSavingKey(group.group_key);
    setGroupActionError('');
    setGroupActionMessage('');
    try {
      const result = await tradeJournalGruppeLoeschen(group.group_key);
      await refreshTradeJournalView();
      cancelGroupEdit(group.group_key);
      setOpenGroupIds(ids => ids.filter(id => id !== group.id));
      setGroupActionMessage(`${result?.affected_fills || 0} Einträge für ${group.symbol || 'den Trade'} gelöscht.`);
    } catch (err) {
      setGroupActionError(err?.message || 'Trade konnte nicht gelöscht werden');
    } finally {
      setGroupDeleteSavingKey('');
    }
  };

  // Filtered data
  const filteredGroups = useMemo(() => {
    return tradeJournalGroups.map(group => projectJournalGroup(group, filterFrom, filterTo, dateFilterBasis)).filter(g => {
      if (!g || (!showPaperTrades && isPaperTradeJournalGroup(g))) return false;
      if (statusFilter !== 'ALL' && g.status !== statusFilter) return false;
      if (instrumentFilter !== 'ALL' && getGroupCategory(g) !== instrumentFilter) return false;
      if (setupFilter !== 'ALL') {
        const assignedIds = Array.isArray(g.setups) ? g.setups.map(setup => String(setup.id)) : [];
      if (setupFilter === 'NONE') {
          if (assignedIds.length > 0) return false;
        } else if (!assignedIds.includes(String(setupFilter))) {
          return false;
        }
      }
      if (!matchesSymbolFilter(g, debouncedSymbolFilter, exactSymbolFilter)) return false;
      return true;
    });
  }, [tradeJournalGroups, showPaperTrades, filterFrom, filterTo, dateFilterBasis, statusFilter, instrumentFilter, debouncedSymbolFilter, exactSymbolFilter, setupFilter]);

  const livePnlByGroupKey = useMemo(() => {
    const map = new Map();
    for (const row of (tradeJournalLivePnl?.rows || [])) {
      if (row?.group_key) map.set(row.group_key, row);
    }
    return map;
  }, [tradeJournalLivePnl]);

  const livePnlExternalTrades = useMemo(() => groupExternalTrades(
    (Array.isArray(tradeJournalLivePnl?.external_trades) ? tradeJournalLivePnl.external_trades : [])
      .filter(item => showPaperTrades || !isPaperExternalTrade(item))
  ), [tradeJournalLivePnl, showPaperTrades]);

  const filteredCashTransactions = useMemo(() => {
    return (tradeJournalCashTransactions || []).filter((transaction) => {
      if (!inRange(transaction.trade_date, filterFrom, filterTo)) return false;
      if (debouncedSymbolFilter.trim()) {
        const haystack = `${transaction.symbol || ''} ${transaction.description || ''}`.toUpperCase();
        const filters = debouncedSymbolFilter
          .split(/[\s,;\n]+/)
          .map(value => value.trim().toUpperCase())
          .filter(Boolean);
        if (filters.length > 0 && !filters.some((textFilter) => {
          const symbol = String(transaction.symbol || '').toUpperCase();
          const firstToken = symbol.split(/\s+/)[0] || '';
          if (exactSymbolFilter) return symbol === textFilter || firstToken === textFilter;
          return haystack.includes(textFilter);
        })) {
          return false;
        }
      }
      return true;
    });
  }, [tradeJournalCashTransactions, filterFrom, filterTo, debouncedSymbolFilter, exactSymbolFilter]);

  const summarizedCashStats = useMemo(() => {
    if (!tradeJournalCashSummary || typeof tradeJournalCashSummary !== 'object') return null;
    const byType = {};
    (Array.isArray(tradeJournalCashSummary.by_type) ? tradeJournalCashSummary.by_type : []).forEach((entry) => {
      const type = entry?.transaction_type || 'Other';
      byType[type] = {
        totalBase: Number(entry?.total_base || 0),
        totalRaw: Number(entry?.total_raw || 0),
        countWithBase: Number(entry?.count_with_base || 0),
        count: Number(entry?.count || 0),
      };
    });
    return {
      totalBase: Number(tradeJournalCashSummary.total_base || 0),
      accountingTotalBase: Number(tradeJournalCashSummary.accounting_total_base || 0),
      hasFullBaseConversion: !!tradeJournalCashSummary.has_full_base_conversion,
      missingBaseCount: Number(tradeJournalCashSummary.missing_base_count || 0),
      totalCount: Number(tradeJournalCashSummary.total_count || 0),
      byType,
    };
  }, [tradeJournalCashSummary]);

  const accountBaseCurrency = useMemo(() => (
    tradeJournalStatementSummary?.base_currency
    || tradeJournalForexPnl.find(r => r.currency_base)?.currency_base
    || null
  ), [tradeJournalStatementSummary, tradeJournalForexPnl]);
  const displayBaseCurrency = accountBaseCurrency || 'EUR';

  // Forex P&L filtered to the active date range and deduplicated to the best
  // matching row per source/year so later partial imports do not stack.
  const filteredForexPnl = useMemo(() => {
    if (!tradeJournalForexPnl || tradeJournalForexPnl.length === 0) return 0;
    const selected = new Map();
    tradeJournalForexPnl.forEach((row) => {
      if (!forexRowWithinRange(row, filterFrom, filterTo)) return;
      const key = `${row.source_kind || 'unknown'}:${row.year}`;
      const current = selected.get(key);
      if (!current || isBetterForexRow(row, current)) {
        selected.set(key, row);
      }
    });
    return Array.from(selected.values()).reduce((sum, row) => sum + (row.realized_pnl_base ?? 0), 0);
  }, [tradeJournalForexPnl, filterFrom, filterTo]);

  // Stats (based strictly on base-currency amounts so all displayed totals are EUR/base only)
  const localStats = useMemo(() => {
    const closed = filteredGroups.filter(g => g.status !== 'OPEN');
    const open = filteredGroups.filter(g => g.status === 'OPEN');
    const assigned = closed.filter(g => g.status === 'ASSIGNED').length;
    const realized = filteredGroups.filter(g => journalEvents(g).length > 0);
    const converted = realized.filter(g => journalPerformance(g).net != null);
    const baseClosed = closed.filter(g => journalPerformance(g).net != null);
    const missingBaseTradeCount = realized.length - converted.length;
    const winningClosed = baseClosed.filter(g => Number(journalPerformance(g).net || 0) > 0);
    const losingClosed = baseClosed.filter(g => Number(journalPerformance(g).net || 0) < 0);
    const wins = winningClosed.length;
    const losses = losingClosed.length;
    const evaluatedClosed = wins + losses;
    const derivedNetPnl = converted.reduce((sum, g) => sum + journalPerformance(g).net, 0);
    const derivedCommissions = converted.reduce((sum, g) => sum + journalPerformance(g).commissions, 0);
    const hasFullBaseConversion = realized.length > 0 && missingBaseTradeCount === 0;
    const derivedGrossPnl = derivedNetPnl + derivedCommissions;
    const grossProfit = winningClosed.reduce((sum, g) => sum + Number(journalPerformance(g).net || 0), 0);
    const grossLossAbs = losingClosed.reduce((sum, g) => sum + Math.abs(Number(journalPerformance(g).net || 0)), 0);
    const averageWin = wins > 0 ? grossProfit / wins : null;
    const averageLoss = losses > 0 ? grossLossAbs / losses : null;
    const payoffRatio = averageWin != null && averageLoss ? averageWin / averageLoss : null;
    const profitFactor = grossProfit > 0 && grossLossAbs > 0 ? grossProfit / grossLossAbs : null;
    const expectancy = evaluatedClosed > 0 ? baseClosed.reduce((sum, g) => sum + journalPerformance(g).net, 0) / evaluatedClosed : null;
    // Keep Net P&L as the sum of trade groups only. Forex P&L is shown separately
    // so the headline stays a pure trade-journal number.
    const totalNetPnl = derivedNetPnl;
    const totalCommissions = derivedCommissions;
    const totalGrossPnl = derivedGrossPnl;
    const forexPnl = filteredForexPnl;
    const winRate = evaluatedClosed > 0 ? (wins / evaluatedClosed) * 100 : null;
    return {
      closed: closed.length,
      open: open.length,
      wins,
      losses,
      assigned,
      evaluatedClosed,
      winRate,
      averageWin,
      averageLoss,
      payoffRatio,
      profitFactor,
      expectancy,
      totalGrossPnl,
      totalNetPnl,
      totalCommissions,
      forexPnl,
      hasFullBaseConversion,
      missingBaseTradeCount,
      filteredGroupCount: filteredGroups.length,
      averagePricePerShare: null,
      averagePricePerShareQuantity: null,
    };
  }, [filteredGroups, filteredForexPnl]);

  const localSetupAnalytics = useMemo(() => {
    const map = new Map();
    let missingBaseCount = 0;
    filteredGroups.forEach((group) => {
      (Array.isArray(group.setups) ? group.setups : []).forEach((setup) => {
        if (!setup?.id) return;
        if (!map.has(setup.id)) {
          map.set(setup.id, {
            id: setup.id,
            name: setup.name,
            description: setup.description || '',
            trades: 0,
            closed: 0,
            open: 0,
            wins: 0,
            losses: 0,
            assigned: 0,
            breakeven: 0,
            netPnl: 0,
            commissions: 0,
          });
        }
        const entry = map.get(setup.id);
        entry.trades += 1;
        if (group.status === 'OPEN') entry.open += 1;
        else entry.closed += 1;
        if (group.status === 'WIN') entry.wins += 1;
        if (group.status === 'LOSS') entry.losses += 1;
        if (group.status === 'ASSIGNED') entry.assigned += 1;
        if (group.status === 'BREAKEVEN') entry.breakeven += 1;
        const performance = journalPerformance(group);
        if (performance.net != null) {
          entry.netPnl += performance.net;
          entry.commissions += performance.commissions;
        } else if (journalEvents(group).length) missingBaseCount += 1;
      });
    });
    const rows = Array.from(map.values())
      .map((entry) => ({
        ...entry,
        winRate: (entry.wins + entry.losses) > 0 ? (entry.wins / (entry.wins + entry.losses)) * 100 : null,
      }))
      .sort((a, b) => {
        if (b.netPnl !== a.netPnl) return b.netPnl - a.netPnl;
        if (b.trades !== a.trades) return b.trades - a.trades;
        return a.name.localeCompare(b.name, 'de');
      });
    return { rows, missingBaseCount };
  }, [filteredGroups]);

  const localPnlChart = useMemo(() => buildPnlChartData(filteredGroups, pnlChartMode), [filteredGroups, pnlChartMode]);

  const stats = useMemo(() => {
    if (!tradeJournalOverview?.stats) return localStats;
    return {
      closed: tradeJournalOverview.stats.closed ?? 0,
      open: tradeJournalOverview.stats.open ?? 0,
      wins: tradeJournalOverview.stats.wins ?? 0,
      losses: tradeJournalOverview.stats.losses ?? 0,
      assigned: tradeJournalOverview.stats.assigned ?? 0,
      evaluatedClosed: tradeJournalOverview.stats.evaluated_closed ?? 0,
      winRate: tradeJournalOverview.stats.win_rate ?? null,
      averageWin: tradeJournalOverview.stats.average_win ?? null,
      averageLoss: tradeJournalOverview.stats.average_loss ?? null,
      payoffRatio: tradeJournalOverview.stats.payoff_ratio ?? null,
      profitFactor: tradeJournalOverview.stats.profit_factor ?? null,
      expectancy: tradeJournalOverview.stats.expectancy ?? null,
      totalGrossPnl: tradeJournalOverview.stats.total_gross_pnl ?? 0,
      totalNetPnl: tradeJournalOverview.stats.total_net_pnl ?? 0,
      totalCommissions: tradeJournalOverview.stats.total_commissions ?? 0,
      forexPnl: tradeJournalOverview.stats.forex_pnl ?? 0,
      hasFullBaseConversion: !!tradeJournalOverview.stats.has_full_base_conversion,
      missingBaseTradeCount: tradeJournalOverview.stats.missing_base_trade_count ?? 0,
      filteredGroupCount: tradeJournalOverview.stats.filtered_group_count ?? 0,
      averagePricePerShare: tradeJournalOverview.stats.average_price_per_share_base ?? null,
      averagePricePerShareQuantity: tradeJournalOverview.stats.average_price_per_share_quantity ?? null,
      averagePricePerShareRaw: tradeJournalOverview.stats.average_price_per_share_raw ?? null,
      averagePricePerShareCurrency: tradeJournalOverview.stats.average_price_per_share_currency ?? null,
    };
  }, [tradeJournalOverview, localStats]);

  const livePnlStats = useMemo(() => ({
    total: Number(tradeJournalLivePnl?.total_live_pnl_base || 0),
    priced: Number(tradeJournalLivePnl?.priced_groups || 0),
    approximate: Number(tradeJournalLivePnl?.approximate_groups || 0),
    skipped: Number(tradeJournalLivePnl?.skipped_groups || 0),
    openCount: Number(stats.open || 0),
    fetchedAt: tradeJournalLivePnl?.as_of || null,
    currentCloseValueBase: Array.isArray(tradeJournalLivePnl?.rows)
      ? tradeJournalLivePnl.rows.reduce((sum, row) => sum + Number(row?.current_close_value_base || 0), 0)
      : 0,
  }), [tradeJournalLivePnl, stats.open]);

  const currentCashBase = useMemo(() => {
    if (!tradeJournalStatementSummary || tradeJournalStatementSummary.current_cash_base == null) return null;
    const value = Number(tradeJournalStatementSummary.current_cash_base);
    return Number.isFinite(value) ? value : null;
  }, [tradeJournalStatementSummary]);

  const estimatedDepotValueBase = useMemo(() => {
    if (currentCashBase == null) return null;
    if (livePnlStats.priced <= 0) return null;
    return currentCashBase + livePnlStats.currentCloseValueBase;
  }, [currentCashBase, livePnlStats.currentCloseValueBase, livePnlStats.priced]);

  const statHints = useMemo(() => ({
    tradesTotal: 'Anzahl aller aktuell gefilterten Journal-Gruppen. Berechnung: Geschlossen + Offen.',
    closed: 'Anzahl der aktuell gefilterten Gruppen mit Status ungleich OPEN.',
    open: 'Anzahl der aktuell gefilterten Gruppen mit Status OPEN.',
    liveOpen: `Summe der Live P&L aller aktuell offenen und live bepreisten Trades. Nicht live bepreiste Trades sind nicht enthalten. Berechnung: Summe live_pnl_base in ${displayBaseCurrency}.`,
    assigned: 'Anzahl geschlossener Gruppen mit Status ASSIGNED.',
    winRate: 'Trefferquote auf Basis der tatsächlichen Netto-P&L geschlossener Trades in Basiswährung. Gezählt werden nur Trades mit positiver oder negativer P&L, Breakeven bleibt außen vor.',
    averageWin: `Durchschnittlicher Netto-Gewinn aller Gewinner-Trades in ${displayBaseCurrency}.`,
    averageLoss: `Durchschnittlicher Netto-Verlust aller Verlierer-Trades in ${displayBaseCurrency}. Der Wert wird als positiver Betrag gezeigt.`,
    payoffRatio: 'Verhältnis aus durchschnittlichem Gewinn zu durchschnittlichem Verlust. Über 1.0 bedeutet: Gewinner sind im Schnitt größer als Verlierer.',
    profitFactor: 'Bruttogewinne geteilt durch Bruttoverluste. Über 1.0 ist profitabel, unter 1.0 verlustreich.',
    expectancy: `Erwartungswert pro bewertetem geschlossenem Trade in ${displayBaseCurrency}. Berechnung: Net P&L / (Gewinner + Verlierer).`,
    averagePricePerShare: `Effektiver Durchschnittspreis pro aktuell offenem Stück im gefilterten Wert.${stats.averagePricePerShareRaw != null && stats.averagePricePerShareCurrency ? ` Anzeige zuerst in ${stats.averagePricePerShareCurrency}, zusätzlich in ${displayBaseCurrency}.` : ` Anzeige in ${displayBaseCurrency}.`} Berechnung: offene Aktienbasis minus realisierte Gewinne/Verluste im gefilterten Zeitraum, geteilt durch die offenen Stücke.${stats.averagePricePerShareQuantity ? ` Offene Stückzahl: ${Number(stats.averagePricePerShareQuantity).toLocaleString('de-DE')}.` : ''}`,
    realized: `Realisierte Brutto-P&L der Schließungen im gewählten Zeitraum, einschließlich Teilverkäufen. Netto-P&L plus zugeordnete Kommissionen.`,
    commissions: `Den realisierten Schließungen zugeordnete Einstiegs- und Ausstiegskosten. In der Anzeige negativ dargestellt.`,
    forex: `Separat geführte Forex-P&L aus den importierten Statement-Zeilen. Nicht in Net P&L enthalten.`,
    net: `Realisierte Netto-P&L im gewählten Zeitraum, einschließlich Teilverkäufen. Verkaufsergebnis nach anteiligen Anschaffungskosten und Gebühren.`,
    dividends: 'Summe der symbol- und datumsgefilterten Dividenden in EUR.',
    withholdingTax: 'Summe der symbol- und datumsgefilterten Quellensteuer in EUR.',
    pilDividends: 'Summe der Payment In Lieu Of Dividends in EUR.',
    netInterest: 'Netto-Zinsen in EUR. Berechnung: Broker Interest Received + Broker Interest Paid.',
    otherFees: 'Summe sonstiger Gebühren in EUR.',
    currentCash: `Aktueller Cash-Bestand aus dem neuesten importierten Statement-Cash-Report. Nicht symbol- oder datumsgefiltert. Berechnung: Wert aus „${tradeJournalStatementSummary?.current_cash_source || 'Ending Settled Cash'}“ in ${displayBaseCurrency}.`,
    openPositionsValue: `Signierter Marktwert der aktuell bepreisten offenen Positionen in ${displayBaseCurrency}. Berechnung: Summe(current_close_value_base). Longs zählen positiv, Shorts negativ.${livePnlStats.approximate > 0 ? ` ${livePnlStats.approximate} offene Trades sind darin nur näherungsweise bewertet.` : ''}${livePnlStats.skipped > 0 ? ` ${livePnlStats.skipped} offene Trades ohne Live-Preis sind darin nicht enthalten.` : ''}`,
    estimatedDepotValue: `Geschätzter Depotwert aus aktuellem Cash plus signiertem Marktwert offener Positionen. Berechnung: Cash aktuell + Summe(current_close_value_base) in ${displayBaseCurrency}. Longs zählen positiv, Shorts negativ.${livePnlStats.approximate > 0 ? ` ${livePnlStats.approximate} offene Trades sind darin nur näherungsweise bewertet.` : ''}${livePnlStats.skipped > 0 ? ` ${livePnlStats.skipped} offene Trades ohne Live-Preis sind darin nicht enthalten.` : ''}`,
    accountTotal: 'Kumulierte Cash-/Kontobewegungen in EUR ohne FX Conversion. Berechnung: Dividenden + PIL + Zinsen + Steuern + Gebühren + weitere Cash-Effekte, exkl. FX Conversion.',
    totalResult: `Kumuliertes Journal-Ergebnis aus Trades und Cash-Bewegungen. Berechnung: Net P&L + Cash-Bewegungen kumuliert in ${displayBaseCurrency}.`,
  }), [displayBaseCurrency, tradeJournalStatementSummary, livePnlStats.approximate, livePnlStats.skipped, stats.averagePricePerShareQuantity, stats.averagePricePerShareRaw, stats.averagePricePerShareCurrency]);

  // Cash transactions grouped by type (filtered)
  const cashByType = useMemo(() => {
    const map = {};
    filteredCashTransactions.forEach(t => {
      const type = t.transaction_type || 'Other';
      if (!map[type]) map[type] = [];
      map[type].push(t);
    });
    return map;
  }, [filteredCashTransactions]);

  const cashTypes = useMemo(() => {
    const known = CASH_TXN_TYPES.filter(t => cashByType[t]);
    const unknown = Object.keys(cashByType).filter(t => !CASH_TXN_TYPES.includes(t));
    return [...known, ...unknown];
  }, [cashByType]);

  // Aggregate cash transactions in base currency (EUR)
  const localCashStats = useMemo(() => {
    let totalBase = 0;
    let accountingTotalBase = 0;
    let countWithBase = 0;
    const byType = {};
    const excludedFromAccountTotal = new Set(['FX Conversion']);
    filteredCashTransactions.forEach(t => {
      const type = t.transaction_type || 'Other';
      if (!byType[type]) byType[type] = { totalBase: 0, totalRaw: 0, countWithBase: 0, count: 0 };
      byType[type].count += 1;
      byType[type].totalRaw += t.amount ?? 0;
      if (t.amount_base != null) {
        byType[type].totalBase += t.amount_base;
        byType[type].countWithBase += 1;
        totalBase += t.amount_base;
        if (!excludedFromAccountTotal.has(type)) {
          accountingTotalBase += t.amount_base;
        }
        countWithBase += 1;
      }
    });
    const hasFullBaseConversion = filteredCashTransactions.length > 0 && countWithBase === filteredCashTransactions.length;
    return {
      totalBase,
      accountingTotalBase,
      hasFullBaseConversion,
      missingBaseCount: Math.max(0, filteredCashTransactions.length - countWithBase),
      totalCount: filteredCashTransactions.length,
      byType,
    };
  }, [filteredCashTransactions]);

  const cashStats = useMemo(() => (
    summarizedCashStats || localCashStats
  ), [summarizedCashStats, localCashStats]);

  const setupAnalytics = useMemo(() => {
    if (!tradeJournalOverview) return localSetupAnalytics;
    return {
      rows: Array.isArray(tradeJournalOverview.setup_analytics)
        ? tradeJournalOverview.setup_analytics.map((entry) => ({
          id: entry.id,
          name: entry.name,
          description: entry.description || '',
          trades: entry.trades ?? 0,
          closed: entry.closed ?? 0,
          open: entry.open ?? 0,
          wins: entry.wins ?? 0,
          losses: entry.losses ?? 0,
          assigned: entry.assigned ?? 0,
          breakeven: entry.breakeven ?? 0,
          netPnl: entry.net_pnl ?? 0,
          commissions: entry.commissions ?? 0,
          winRate: entry.win_rate ?? null,
        }))
        : [],
      missingBaseCount: tradeJournalOverview.setup_missing_base_count ?? 0,
    };
  }, [tradeJournalOverview, localSetupAnalytics]);

  const pnlChart = useMemo(() => {
    const normalizeChart = (chart) => ({
      rows: Array.isArray(chart?.rows) ? chart.rows : [],
      skippedGroups: chart?.skipped_groups ?? chart?.skippedGroups ?? 0,
    });
    if (!tradeJournalOverview) return normalizeChart(localPnlChart);
    if (pnlChartMode === 'setup') return normalizeChart(tradeJournalOverview.setup_chart);
    if (pnlChartMode === 'year') return normalizeChart(tradeJournalOverview.year_chart);
    return normalizeChart(tradeJournalOverview.month_chart);
  }, [tradeJournalOverview, localPnlChart, pnlChartMode]);

  const prioritizeOpenTrades = statusFilter === 'ALL' && groupSort.key === 'open_date' && groupSort.direction === 'desc';

  const sortedGroups = useMemo(() => (
    (() => {
      const sorted = sortBySpec(filteredGroups, groupSort, (group, key) => {
        switch (key) {
          case 'status': return group.status;
          case 'open_date': return group.open_date || '';
          case 'symbol': return group.symbol || '';
          case 'entry': return Number.isFinite(Number(group.entry)) ? Number(group.entry) : null;
          case 'exit': return Number.isFinite(Number(group.exit)) ? Number(group.exit) : null;
          case 'size': return Number.isFinite(Number(group.size)) ? Number(group.size) : null;
          case 'return_amount': return journalPerformance(group).net;
          case 'return_percent': return Number.isFinite(Number(group.return_percent)) ? Number(group.return_percent) : null;
          case 'side': return group.side || '';
          case 'call_put': return group.call_put || '';
          case 'close_date': return group.close_date || '';
          case 'hold_seconds': return Number.isFinite(Number(group.hold_seconds)) ? Number(group.hold_seconds) : null;
          default: return group[key] ?? null;
        }
      });
      if (!prioritizeOpenTrades) return sorted;
      const open = sorted.filter(group => group.status === 'OPEN');
      const other = sorted.filter(group => group.status !== 'OPEN');
      return [...open, ...other];
    })()
  ), [filteredGroups, groupSort, prioritizeOpenTrades]);

  const currentTradePageSize = tradeListMode === '100' ? 100 : 50;
  const currentFilterParams = useMemo(() => ({
    from: filterFrom,
    to: filterTo,
    dateBasis: dateFilterBasis,
    status: statusFilter,
    instrument: instrumentFilter,
    symbol: debouncedSymbolFilter,
    exactSymbol: exactSymbolFilter,
    setupId: setupFilter,
  }), [filterFrom, filterTo, dateFilterBasis, statusFilter, instrumentFilter, debouncedSymbolFilter, exactSymbolFilter, setupFilter]);

  const refreshTradeJournalView = useCallback(async () => {
    if (tradeListMode === 'all') {
      await Promise.all([
        ladeTradeJournalOverview(currentFilterParams),
        ladeTradeJournalCashSummary(currentFilterParams),
        ladeTradeJournalGroups({
          sortKey: groupSort.key,
          sortDirection: groupSort.direction,
          prioritizeOpen: prioritizeOpenTrades,
          ...currentFilterParams,
        }),
      ]);
      return;
    }
    await ladeTradeJournalDashboard({
      limit: currentTradePageSize,
      offset: tradePageOffset,
      sortKey: groupSort.key,
      sortDirection: groupSort.direction,
      prioritizeOpen: prioritizeOpenTrades,
      ...currentFilterParams,
    });
  }, [
    currentFilterParams,
    currentTradePageSize,
    tradeListMode,
    tradePageOffset,
    groupSort,
    prioritizeOpenTrades,
    ladeTradeJournalOverview,
    ladeTradeJournalCashSummary,
    ladeTradeJournalGroups,
    ladeTradeJournalDashboard,
  ]);

  useEffect(() => {
    refreshTradeJournalViewRef.current = refreshTradeJournalView;
    return () => { refreshTradeJournalViewRef.current = null; };
  }, [refreshTradeJournalView]);

  useEffect(() => {
    setTradePageOffset(0);
  }, [tradeListMode, groupSort, filterFrom, filterTo, dateFilterBasis, statusFilter, instrumentFilter, symbolFilter, exactSymbolFilter, setupFilter, showPaperTrades, tradeJournalSourceFilter]);

  useEffect(() => {
    setSyncLogOpen(false);
  }, [tradeJournalLastSyncAt, tradeJournalLastSyncImportedCount, tradeJournalLastSyncSkippedCount]);

  useEffect(() => {
    if (setupFilter === 'ALL' || setupFilter === 'NONE') return;
    const exists = (tradeJournalSetups || []).some(setup => String(setup.id) === String(setupFilter));
    if (!exists) setSetupFilter('ALL');
  }, [setupFilter, tradeJournalSetups]);

  useEffect(() => {
    if (!cashPanelOpen) return;
    ladeTradeJournalCashTransactions(currentFilterParams);
  }, [cashPanelOpen, currentFilterParams, ladeTradeJournalCashTransactions]);

  useEffect(() => {
    if (tradeListMode !== 'all') return;
    ladeTradeJournalOverview(currentFilterParams);
  }, [currentFilterParams, tradeListMode, tradeJournalSourceFilter, ladeTradeJournalOverview]);

  useEffect(() => {
    if (tradeListMode !== 'all') return;
    ladeTradeJournalCashSummary(currentFilterParams);
  }, [currentFilterParams, tradeListMode, tradeJournalSourceFilter, ladeTradeJournalCashSummary]);

  useEffect(() => {
    if (tradeListMode !== 'all') return;
    if ((tradeJournalGroups || []).length > 0) return;
    ladeTradeJournalGroups({
      sortKey: groupSort.key,
      sortDirection: groupSort.direction,
      prioritizeOpen: prioritizeOpenTrades,
      ...currentFilterParams,
    });
  }, [tradeListMode, tradeJournalGroups, ladeTradeJournalGroups, groupSort, prioritizeOpenTrades, currentFilterParams]);

  const visibleGroups = useMemo(() => {
    const pageItems = Array.isArray(tradeJournalGroupPage?.items) ? tradeJournalGroupPage.items : [];
    const filteredPageItems = showPaperTrades ? pageItems : pageItems.filter(group => !isPaperTradeJournalGroup(group));
    if (tradeListMode === 'all') return sortedGroups;
    return filteredPageItems;
  }, [sortedGroups, tradeListMode, tradeJournalGroupPage, showPaperTrades]);

  useEffect(() => {
    const visibleKeys = new Set(visibleGroups.map(group => group.group_key).filter(Boolean));
    setSelectedGroupKeys(current => current.filter(key => visibleKeys.has(key)));
  }, [visibleGroups]);

  useEffect(() => {
    if (tradeListMode === 'all') return;
    ladeTradeJournalDashboard({
      limit: currentTradePageSize,
      offset: tradePageOffset,
      from: filterFrom,
      to: filterTo,
      dateBasis: dateFilterBasis,
      status: statusFilter,
      instrument: instrumentFilter,
      symbol: debouncedSymbolFilter,
      exactSymbol: exactSymbolFilter,
      setupId: setupFilter,
      sortKey: groupSort.key,
      sortDirection: groupSort.direction,
      prioritizeOpen: prioritizeOpenTrades,
    });
    return () => invalidateTradeJournalDashboard();
  }, [
    tradeListMode,
    currentTradePageSize,
    tradePageOffset,
    filterFrom,
    filterTo,
    dateFilterBasis,
    statusFilter,
    instrumentFilter,
    debouncedSymbolFilter,
    exactSymbolFilter,
    setupFilter,
    groupSort,
    prioritizeOpenTrades,
    tradeJournalSourceFilter,
    ladeTradeJournalDashboard,
    invalidateTradeJournalDashboard,
  ]);

  const displayedGroupTotal = tradeListMode === 'all'
    ? filteredGroups.length
    : Number(stats.filteredGroupCount || tradeJournalGroupPage?.total || 0);

  const visibleGroupKeys = visibleGroups.map(group => group.group_key).filter(Boolean);
  const allVisibleSelected = visibleGroupKeys.length > 0 && visibleGroupKeys.every(key => selectedGroupKeys.includes(key));
  const currentPageNumber = tradeListMode === 'all' ? 1 : Math.floor(tradePageOffset / currentTradePageSize) + 1;
  const totalPageCount = tradeListMode === 'all' ? 1 : Math.max(1, Math.ceil(displayedGroupTotal / currentTradePageSize));
  const canPageBackward = tradeListMode !== 'all' && tradePageOffset > 0;
  const canPageForward = tradeListMode !== 'all' && (tradePageOffset + currentTradePageSize) < displayedGroupTotal;

  const renderHeaderButton = (label, sortKey, align = 'left') => {
    const isActive = groupSort.key === sortKey;
    const indicator = !isActive ? '↕' : groupSort.direction === 'asc' ? '↑' : '↓';
    return (
      <button
        type="button"
        onClick={() => toggleGroupSort(sortKey)}
        style={{
          background: 'transparent', border: 'none', padding: 0, margin: 0,
          color: isActive ? COLORS.text : COLORS.muted,
          cursor: 'pointer', font: 'inherit',
          display: 'inline-flex', alignItems: 'center',
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
          gap: '6px', width: '100%', textAlign: align,
          textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '11px', fontWeight: 700,
        }}
      >
        <span>{label}</span>
        <span style={{ fontSize: '10px', color: isActive ? COLORS.cyan : COLORS.muted }}>{indicator}</span>
      </button>
    );
  };

  const isSyncing = tradeJournalSyncLoading || (tradeJournalSyncStatus && tradeJournalSyncStatus !== 'done' && tradeJournalSyncStatus !== 'failed');

  const inputStyle = {
    background: '#ffffff0a',
    border: `1px solid ${COLORS.border}`,
    borderRadius: '7px',
    color: COLORS.text,
    padding: '5px 10px',
    fontSize: '13px',
    outline: 'none',
    colorScheme: 'dark',
  };

  const manualFieldCardStyle = {
    display: 'grid',
    gap: '6px',
    padding: '10px',
    background: '#ffffff06',
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    minWidth: 0,
  };

  const manualFieldLabelStyle = {
    fontSize: '11px',
    color: COLORS.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: 700,
    cursor: 'help',
  };

  const manualFieldInputStyle = {
    ...inputStyle,
    padding: '8px 10px',
    background: '#ffffff08',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    minHeight: '42px',
  };

  const renderManualField = (label, hint, field) => (
    <div style={manualFieldCardStyle}>
      <div title={hint} style={manualFieldLabelStyle}>{label}</div>
      {field}
    </div>
  );

  const hasManualOptionRows = manualTradeRows.some(row => row.instrument_type === 'OPTION');

  const compactMainCell = {
    paddingTop: '6px',
    paddingBottom: '6px',
    fontSize: '11px',
    lineHeight: 1.2,
  };

  return (
    <section style={{ display: 'grid', gap: '18px', minWidth: 0 }}>

      {/* Header + Sync */}
      <Card style={{ marginBottom: 0 }}>
        <div style={{ display: 'grid', gap: '16px', marginBottom: '8px' }}>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap', alignItems: 'center', marginTop: '6px', marginBottom: '0', overflowX: 'auto', paddingBottom: '6px' }}>
            <Btn onClick={refreshTradeJournalView} color="ghost">Neu laden</Btn>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml,.csv"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) importiereTradeJournalDatei(file);
                e.target.value = '';
              }}
            />
            <Btn onClick={() => fileInputRef.current?.click()} color="ghost" disabled={!!tradeJournalSyncLoading}>
              Datei importieren
            </Btn>
            <Btn onClick={starteTradeJournalSync} color="ghost" disabled={!!tradeJournalSyncLoading}>
              {tradeJournalSyncLoading ? 'Sync läuft…' : 'IBKR Sync'}
            </Btn>
            <span style={{ color: COLORS.border, fontSize: '14px', padding: '0 2px' }}>|</span>
            <Btn
              onClick={() => aktualisiereTradeJournalLivePnl({
                ...currentFilterParams,
              })}
              color="ghost"
              disabled={!!tradeJournalLivePnlLoading || stats.open === 0}
            >
              {tradeJournalLivePnlLoading ? 'Live P&L…' : 'Live P&L'}
            </Btn>
            <span style={{ color: COLORS.border, fontSize: '14px', padding: '0 2px' }}>|</span>
            <button
              type="button"
              onClick={() => setFilterPanelOpen(open => !open)}
              style={{
                background: filterPanelOpen ? `${COLORS.cyan}18` : '#ffffff08',
                border: `1px solid ${filterPanelOpen ? COLORS.cyan : COLORS.border}`,
                color: filterPanelOpen ? COLORS.cyan : COLORS.muted,
                borderRadius: '8px',
                padding: '8px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Filter
            </button>
            <span style={{ color: COLORS.border, fontSize: '14px', padding: '0 2px' }}>|</span>
            <button
              type="button"
              onClick={() => setSetupPanelOpen(open => !open)}
              style={{
                background: setupPanelOpen ? `${COLORS.cyan}18` : '#ffffff08',
                border: `1px solid ${setupPanelOpen ? COLORS.cyan : COLORS.border}`,
                color: setupPanelOpen ? COLORS.cyan : COLORS.muted,
                borderRadius: '8px',
                padding: '8px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Setups
            </button>
            <button
              type="button"
              onClick={() => setSetupAnalyticsPanelOpen(open => !open)}
              style={{
                background: setupAnalyticsPanelOpen ? `${COLORS.cyan}18` : '#ffffff08',
                border: `1px solid ${setupAnalyticsPanelOpen ? COLORS.cyan : COLORS.border}`,
                color: setupAnalyticsPanelOpen ? COLORS.cyan : COLORS.muted,
                borderRadius: '8px',
                padding: '8px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Setups P&L
            </button>
            <span style={{ color: COLORS.border, fontSize: '14px', padding: '0 2px' }}>|</span>
            <button
              type="button"
              onClick={() => setCashPanelOpen(open => !open)}
              style={{
                background: cashPanelOpen ? `${COLORS.cyan}18` : '#ffffff08',
                border: `1px solid ${cashPanelOpen ? COLORS.cyan : COLORS.border}`,
                color: cashPanelOpen ? COLORS.cyan : COLORS.muted,
                borderRadius: '8px',
                padding: '8px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Konto-Bewegungen
            </button>
            <span style={{ color: COLORS.border, fontSize: '14px', padding: '0 2px' }}>|</span>
            <button
              type="button"
              onClick={() => setManualTradePanelOpen(open => !open)}
              style={{
                background: manualTradePanelOpen ? `${COLORS.cyan}18` : '#ffffff08',
                border: `1px solid ${manualTradePanelOpen ? COLORS.cyan : COLORS.border}`,
                color: manualTradePanelOpen ? COLORS.cyan : COLORS.muted,
                borderRadius: '8px',
                padding: '8px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Trade eingeben
            </button>
            <span style={{ color: COLORS.border, fontSize: '14px', padding: '0 2px' }}>|</span>
            <button
              type="button"
              onClick={() => setSyncLogOpen(open => !open)}
              style={{
                background: syncLogOpen ? `${COLORS.cyan}18` : '#ffffff08',
                border: `1px solid ${syncLogOpen ? COLORS.cyan : COLORS.border}`,
                color: syncLogOpen ? COLORS.cyan : COLORS.muted,
                borderRadius: '8px',
                padding: '8px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Sync Log
            </button>
            <div style={{ flex: '1 0 20px', minWidth: '20px' }} />
            <button
              type="button"
              onClick={() => {
                if (!window.confirm('Wirklich alle Trade-Journal-Einträge aus der Datenbank löschen?')) return;
                loescheTradeJournalEintraege();
              }}
              disabled={!!tradeJournalSyncLoading}
              style={{
                background: `${COLORS.red}18`,
                border: `1px solid ${COLORS.red}`,
                color: COLORS.red,
                borderRadius: '8px',
                padding: '8px 10px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: tradeJournalSyncLoading ? 'not-allowed' : 'pointer',
                opacity: tradeJournalSyncLoading ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              Journal löschen
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {isSyncing && (
          <div style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: COLORS.muted, marginBottom: '5px' }}>
              <span>{tradeJournalSyncMessage || 'Sync läuft…'}</span>
              <span>{tradeJournalSyncProgress}%</span>
            </div>
            <div style={{ height: '6px', borderRadius: '3px', background: '#ffffff10', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: '3px', background: COLORS.cyan, width: `${tradeJournalSyncProgress}%`, transition: 'width 0.4s ease' }} />
            </div>
            {(tradeJournalSyncFetchedCount > 0 || tradeJournalSyncImportedCount > 0) && (
              <div style={{ fontSize: '12px', color: COLORS.muted, marginTop: '5px' }}>
                Geladen: {tradeJournalSyncFetchedCount} · Neu: {tradeJournalSyncImportedCount} · Übersprungen: {tradeJournalSyncSkippedCount}
              </div>
            )}
          </div>
        )}

        {!isSyncing && tradeJournalSyncStatus === 'done' && (
          <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: COLORS.green }}>
              ✓ Sync abgeschlossen — {tradeJournalSyncImportedCount} neue Einträge
              {tradeJournalSyncSkippedCount > 0 && `, ${tradeJournalSyncSkippedCount} bereits vorhanden`}
            </div>
            {(tradeJournalLivePnlLoading || livePnlStats.fetchedAt || livePnlStats.skipped > 0 || livePnlStats.priced > 0) && (
              <div style={{ fontSize: '12px', color: COLORS.green }}>
                {tradeJournalLivePnlLoading ? 'Live P&L wird aktualisiert… ' : ''}
                {livePnlStats.fetchedAt
                  ? `Letzte Live-Aktualisierung: ${new Date(livePnlStats.fetchedAt).toLocaleString('de-DE')}. `
                  : 'Live P&L wird nur nach Klick auf „Live P&L aktualisieren“ geladen. '}
                {livePnlStats.openCount > 0 && `${livePnlStats.priced} von ${livePnlStats.openCount} offenen Trades live bepreist.`}
                {livePnlStats.approximate > 0 && ` ${livePnlStats.approximate} offene Trades nur näherungsweise bewertet.`}
                {livePnlStats.skipped > 0 && ` ${livePnlStats.skipped} offene Trades konnten nicht live bepreist werden.`}
              </div>
            )}
          </div>
        )}

        {!isSyncing && tradeJournalSyncStatus !== 'done' && (tradeJournalLivePnlLoading || livePnlStats.fetchedAt || livePnlStats.skipped > 0 || livePnlStats.priced > 0) && (
          <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: COLORS.green }}>
              {tradeJournalLivePnlLoading ? 'Live P&L wird aktualisiert… ' : ''}
              {livePnlStats.fetchedAt
                ? `Letzte Live-Aktualisierung: ${new Date(livePnlStats.fetchedAt).toLocaleString('de-DE')}. `
                : 'Live P&L wird nur nach Klick auf „Live P&L aktualisieren“ geladen. '}
              {livePnlStats.openCount > 0 && `${livePnlStats.priced} von ${livePnlStats.openCount} offenen Trades live bepreist.`}
              {livePnlStats.approximate > 0 && ` ${livePnlStats.approximate} offene Trades nur näherungsweise bewertet.`}
              {livePnlStats.skipped > 0 && ` ${livePnlStats.skipped} offene Trades konnten nicht live bepreist werden.`}
            </div>
          </div>
        )}

        {tradeJournalSyncError && (
          <div style={{ fontSize: '13px', color: COLORS.red, marginBottom: '12px' }}>⚠ {tradeJournalSyncError}</div>
        )}
        {setupError && <div role="alert" style={{ fontSize: '13px', color: COLORS.red, marginBottom: '12px' }}>⚠ {setupError}</div>}
        {setupMessage && <div role="status" style={{ fontSize: '13px', color: COLORS.green, marginBottom: '12px' }}>{setupMessage}</div>}
        {tradeJournalLivePnlError && !tradeJournalLivePnl?.as_of && (
          <div style={{ fontSize: '13px', color: COLORS.red, marginBottom: '12px' }}>⚠ {tradeJournalLivePnlError}</div>
        )}
        {/* Stats bar */}
        {(tradeJournalOverviewLoading
          || tradeJournalOverview
          || tradeJournalCashSummary
          || tradeJournalStatementSummary
          || tradeJournalGroups.length > 0
          || (tradeJournalGroupPage && (tradeJournalGroupPage.total != null || (tradeJournalGroupPage.items || []).length > 0))) && (
          <details open style={{ marginBottom: '12px' }}>
            <summary style={{ cursor: 'pointer', color: COLORS.text, fontSize: '13px', fontWeight: 700, marginBottom: '12px' }}>
              Kennzahlen anzeigen
            </summary>
            <div style={{ marginTop: '12px' }}>
              {(!stats.hasFullBaseConversion || (!cashStats.hasFullBaseConversion && (cashStats.totalCount || 0) > 0)) && (
                <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '14px' }}>
                  {!stats.hasFullBaseConversion && `${stats.missingBaseTradeCount} geschlossene Trades ohne vollständige EUR-Umrechnung sind in den Trade-Summen nicht enthalten.`}
                  {!stats.hasFullBaseConversion && !cashStats.hasFullBaseConversion && (cashStats.totalCount || 0) > 0 ? ' ' : ''}
                  {!cashStats.hasFullBaseConversion && (cashStats.totalCount || 0) > 0 && `${cashStats.missingBaseCount} Konto-Bewegungen ohne EUR-Umrechnung sind in den Cash-Summen nicht enthalten.`}
                </div>
              )}
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <StatItem label="Trades gesamt" value={stats.closed + stats.open} hint={statHints.tradesTotal} COLORS={COLORS} />
              <StatItem label="Geschlossen" value={stats.closed} hint={statHints.closed} COLORS={COLORS} />
              <StatItem label="Offen" value={stats.open} hint={statHints.open} COLORS={COLORS} />
              <StatItem
                label={`Live P&L offen (${displayBaseCurrency})`}
                value={livePnlStats.priced > 0 ? formatMoneyValue(livePnlStats.total, displayBaseCurrency) : '—'}
                color={livePnlStats.priced > 0 ? valueColor(livePnlStats.total) : COLORS.muted}
                hint={statHints.liveOpen}
                COLORS={COLORS}
              />
              {stats.assigned > 0 && <StatItem label="Assigned" value={stats.assigned} color={COLORS.cyan} hint={statHints.assigned} COLORS={COLORS} />}
              <StatItem
                label="Win Rate"
                value={stats.winRate !== null ? `${stats.winRate.toFixed(0)}%` : '—'}
                color={stats.winRate >= 50 ? COLORS.green : COLORS.red}
                hint={statHints.winRate}
                COLORS={COLORS}
              />
              <StatItem label={`Realized P&L (${displayBaseCurrency})`} value={formatMoneyValue(stats.totalGrossPnl, displayBaseCurrency)} color={valueColor(stats.totalGrossPnl)} hint={statHints.realized} COLORS={COLORS} />
              <StatItem label={`Commissions (${displayBaseCurrency})`} value={formatMoneyValue(-stats.totalCommissions, displayBaseCurrency)} color={COLORS.red} hint={statHints.commissions} COLORS={COLORS} />
              {stats.forexPnl !== 0 && (
                <StatItem label={`Forex P&L separat (${displayBaseCurrency})`} value={formatMoneyValue(stats.forexPnl, displayBaseCurrency)} color={valueColor(stats.forexPnl)} hint={statHints.forex} COLORS={COLORS} />
              )}
              <StatItem label={`Net P&L (${displayBaseCurrency})`} value={formatMoneyValue(stats.totalNetPnl, displayBaseCurrency)} color={valueColor(stats.totalNetPnl)} hint={statHints.net} COLORS={COLORS} />
              </div>
              {stats.evaluatedClosed > 0 && (
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', paddingTop: '10px', marginTop: '2px', borderTop: `1px solid ${COLORS.border}` }}>
                  <StatItem
                    label={`Ø Gewinn (${displayBaseCurrency})`}
                    value={formatMoneyValue(stats.averageWin, displayBaseCurrency)}
                    color={valueColor(stats.averageWin ?? 0)}
                    hint={statHints.averageWin}
                    COLORS={COLORS}
                  />
                  <StatItem
                    label={`Ø Verlust (${displayBaseCurrency})`}
                    value={formatMoneyValue(stats.averageLoss, displayBaseCurrency)}
                    color={COLORS.red}
                    hint={statHints.averageLoss}
                    COLORS={COLORS}
                  />
                  <StatItem
                    label="Payoff Ratio"
                    value={stats.payoffRatio != null ? stats.payoffRatio.toFixed(2) : '—'}
                    color={stats.payoffRatio != null ? valueColor(stats.payoffRatio - 1) : COLORS.muted}
                    hint={statHints.payoffRatio}
                    COLORS={COLORS}
                  />
                  <StatItem
                    label="Profit Factor"
                    value={stats.profitFactor != null ? stats.profitFactor.toFixed(2) : '—'}
                    color={stats.profitFactor != null ? valueColor(stats.profitFactor - 1) : COLORS.muted}
                    hint={statHints.profitFactor}
                    COLORS={COLORS}
                  />
                  <StatItem
                    label={`Expectancy (${displayBaseCurrency})`}
                    value={formatMoneyValue(stats.expectancy, displayBaseCurrency)}
                    color={valueColor(stats.expectancy ?? 0)}
                    hint={statHints.expectancy}
                    COLORS={COLORS}
                  />
                </div>
              )}
              {/* Cash transaction stats */}
              {((cashStats.totalCount || 0) > 0 || stats.averagePricePerShare != null) && (
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', paddingTop: '10px', borderTop: `1px solid ${COLORS.border}` }}>
                  {cashStats.byType['Dividends'] && (
                    <StatItem label={`Dividenden (${displayBaseCurrency})`} value={formatMoneyValue(cashStats.byType['Dividends'].totalBase, displayBaseCurrency)} color={valueColor(cashStats.byType['Dividends'].totalBase)} hint={statHints.dividends} COLORS={COLORS} />
                  )}
                  {cashStats.byType['Withholding Tax'] && (
                    <StatItem label={`Quellensteuer (${displayBaseCurrency})`} value={formatMoneyValue(cashStats.byType['Withholding Tax'].totalBase, displayBaseCurrency)} color={COLORS.red} hint={statHints.withholdingTax} COLORS={COLORS} />
                  )}
                  {cashStats.byType['Payment In Lieu Of Dividends'] && (
                    <StatItem label={`PIL Dividends (${displayBaseCurrency})`} value={formatMoneyValue(cashStats.byType['Payment In Lieu Of Dividends'].totalBase, displayBaseCurrency)} color={valueColor(cashStats.byType['Payment In Lieu Of Dividends'].totalBase)} hint={statHints.pilDividends} COLORS={COLORS} />
                  )}
                  {(cashStats.byType['Broker Interest Received'] || cashStats.byType['Broker Interest Paid']) && (
                    <StatItem
                      label={`Zinsen Netto (${displayBaseCurrency})`}
                      value={formatMoneyValue(
                        (cashStats.byType['Broker Interest Received']?.totalBase ?? 0) +
                        (cashStats.byType['Broker Interest Paid']?.totalBase ?? 0),
                        displayBaseCurrency
                      )}
                      color={valueColor(
                        (cashStats.byType['Broker Interest Received']?.totalBase ?? 0) +
                        (cashStats.byType['Broker Interest Paid']?.totalBase ?? 0)
                      )}
                      hint={statHints.netInterest}
                      COLORS={COLORS}
                    />
                  )}
                  {cashStats.byType['Other Fees'] && (
                    <StatItem label={`Andere Gebühren (${displayBaseCurrency})`} value={formatMoneyValue(cashStats.byType['Other Fees'].totalBase, displayBaseCurrency)} color={COLORS.red} hint={statHints.otherFees} COLORS={COLORS} />
                  )}
                  {stats.averagePricePerShare != null && (
                    <StatItem
                      label={`Ø Preis/Stück (${displayBaseCurrency})`}
                      value={
                        stats.averagePricePerShareRaw != null && stats.averagePricePerShareCurrency
                          ? `${formatMoneyValue(stats.averagePricePerShareRaw, stats.averagePricePerShareCurrency)} / ${formatMoneyValue(stats.averagePricePerShare, displayBaseCurrency)}`
                          : formatMoneyValue(stats.averagePricePerShare, displayBaseCurrency)
                      }
                      color={COLORS.text}
                      hint={statHints.averagePricePerShare}
                      COLORS={COLORS}
                    />
                  )}
                </div>
              )}
              {((cashStats.totalCount || 0) > 0 || currentCashBase != null || estimatedDepotValueBase != null) && (
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', paddingTop: '10px', marginTop: '10px', borderTop: `1px solid ${COLORS.border}` }}>
                  {currentCashBase != null && (
                    <StatItem
                      label={`Cash aktuell (${displayBaseCurrency})`}
                      value={formatMoneyValue(currentCashBase, displayBaseCurrency)}
                      color={valueColor(currentCashBase)}
                      hint={statHints.currentCash}
                      COLORS={COLORS}
                    />
                  )}
                  {(livePnlStats.priced > 0 || livePnlStats.approximate > 0) && (
                    <StatItem
                      label={`Offene Positionen (${displayBaseCurrency})`}
                      value={formatMoneyValue(livePnlStats.currentCloseValueBase, displayBaseCurrency)}
                      color={valueColor(livePnlStats.currentCloseValueBase)}
                      hint={statHints.openPositionsValue}
                      COLORS={COLORS}
                    />
                  )}
                  {estimatedDepotValueBase != null && (
                    <StatItem
                      label={`Depotwert ~ (${displayBaseCurrency})`}
                      value={formatMoneyValue(estimatedDepotValueBase, displayBaseCurrency)}
                      color={valueColor(estimatedDepotValueBase)}
                      hint={statHints.estimatedDepotValue}
                      COLORS={COLORS}
                    />
                  )}
                  <StatItem
                      label={`Cash-Bewegungen kumuliert (${displayBaseCurrency})`}
                    value={formatMoneyValue(cashStats.accountingTotalBase, displayBaseCurrency)}
                    color={valueColor(cashStats.accountingTotalBase)}
                    hint={statHints.accountTotal}
                    COLORS={COLORS}
                  />
                  {stats.hasFullBaseConversion && cashStats.hasFullBaseConversion && (
                    <StatItem
                      label={`Journal-Ergebnis kumuliert (${displayBaseCurrency})`}
                      value={formatMoneyValue(stats.totalNetPnl + cashStats.accountingTotalBase, displayBaseCurrency)}
                      color={valueColor(stats.totalNetPnl + cashStats.accountingTotalBase)}
                      hint={statHints.totalResult}
                      COLORS={COLORS}
                    />
                  )}
                </div>
              )}
            </div>
          </details>
        )}
      </Card>

      {syncLogOpen && (
        <Card style={{ marginBottom: 0, padding: '14px 18px' }}>
          <div
            style={{
              background: '#ffffff06',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '10px',
              padding: '10px 12px',
              display: 'grid',
              gap: '8px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ color: COLORS.text, fontSize: '12px', fontWeight: 700 }}>Letzter Sync</div>
              <div style={{ color: COLORS.muted, fontSize: '12px' }}>{formatSyncItemDateTime(tradeJournalLastSyncAt, '')}</div>
            </div>
            {(tradeJournalLastSyncItems || []).length === 0 ? (
              <div style={{ fontSize: '12px', color: COLORS.muted }}>
                {tradeJournalLastSyncImportedCount > 0
                  ? `${tradeJournalLastSyncImportedCount} neue Einträge wurden im letzten Sync gespeichert, konnten aber nicht mehr als Detail-Liste geladen werden.`
                  : `Keine neuen Einträge im letzten Sync. ${tradeJournalLastSyncSkippedCount > 0 ? `${tradeJournalLastSyncSkippedCount} Einträge waren bereits vorhanden.` : ''}`}
              </div>
            ) : (
              (tradeJournalLastSyncItems || []).map((item, index) => {
                const amountValue = item.amount ?? item.price;
                const amountLabel = amountValue === null || amountValue === undefined || Number.isNaN(Number(amountValue))
                  ? '—'
                  : `${Number(amountValue).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${item.currency ? ` ${item.currency}` : ''}`;
                return (
                  <div
                    key={`${item.kind || 'entry'}-${item.label || 'label'}-${item.date || 'date'}-${item.time || 'time'}-${index}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '110px minmax(0, 1fr) 140px 130px',
                      gap: '10px',
                      alignItems: 'center',
                      fontSize: '12px',
                      color: COLORS.text,
                      borderBottom: index < tradeJournalLastSyncItems.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                      paddingBottom: index < tradeJournalLastSyncItems.length - 1 ? '8px' : 0,
                    }}
                  >
                    <div style={{ color: COLORS.cyan, fontWeight: 700 }}>
                      {item.kind === 'Cash' ? 'Cash' : 'Trade'}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.label || 'Unbekannter Eintrag'}
                      </div>
                      <div style={{ color: COLORS.muted }}>
                        {[item.side, item.quantity].filter(value => value !== null && value !== undefined && value !== '').join(' · ') || '—'}
                      </div>
                    </div>
                    <div style={{ color: COLORS.muted }}>
                      {formatSyncItemDateTime(item.date, item.time)}
                    </div>
                    <div style={{ textAlign: 'right', color: COLORS.text }}>
                      {amountLabel}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      )}

      {manualTradePanelOpen && (
        <Card style={{ marginBottom: 0, padding: '16px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ fontSize: '16px', fontWeight: 700 }}>Trade manuell eingeben</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Btn type="button" color="ghost" onClick={() => applyManualVerticalSpreadPreset('C')}>Call Spread</Btn>
              <Btn type="button" color="ghost" onClick={() => applyManualVerticalSpreadPreset('P')}>Put Spread</Btn>
              <Btn type="button" color="ghost" onClick={addManualTradeRow}>{hasManualOptionRows ? 'Leg hinzufügen' : 'Zeile hinzufügen'}</Btn>
              <Btn type="button" color="green" onClick={saveManualTrades} disabled={manualTradeSaving}>
                {manualTradeSaving ? 'Speichert…' : 'Speichern'}
              </Btn>
            </div>
          </div>
          {manualTradeError && <div style={{ fontSize: '13px', color: COLORS.red, marginBottom: '10px' }}>⚠ {manualTradeError}</div>}
          {manualTradeMessage && <div style={{ fontSize: '13px', color: COLORS.green, marginBottom: '10px' }}>✓ {manualTradeMessage}</div>}
          <div style={{ display: 'grid', gap: '10px' }}>
            {manualTradeRows.map((row, index) => (
              <div key={`manual-row-${index}`} style={{ border: `1px solid ${COLORS.border}`, borderRadius: '12px', padding: '12px', background: '#ffffff05' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontSize: '12px', color: COLORS.muted, fontWeight: 700 }}>
                    {row.instrument_type === 'OPTION' ? `Leg ${index + 1}` : `Zeile ${index + 1}`}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeManualTradeRow(index)}
                    style={{ background: 'transparent', border: 'none', color: COLORS.red, fontSize: '12px', cursor: 'pointer' }}
                  >
                    Entfernen
                  </button>
                </div>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
                    {row.instrument_type !== 'OPTION' && renderManualField('Symbol', 'Das gehandelte Instrument, zum Beispiel MSFT.', (
                      <input value={row.symbol} onChange={e => updateManualTradeRow(index, 'symbol', e.target.value.toUpperCase())} placeholder="z. B. MSFT" style={manualFieldInputStyle} />
                    ))}
                    {renderManualField('Aktion', 'Kauf oder Verkauf dieses einzelnen Fills.', (
                      <select value={row.action} onChange={e => updateManualTradeRow(index, 'action', e.target.value)} style={manualFieldInputStyle}>
                        <option value="BUY">BUY</option>
                        <option value="SELL">SELL</option>
                      </select>
                    ))}
                    {renderManualField('Typ', 'Instrumentart des Fills.', (
                      <select value={row.instrument_type} onChange={e => updateManualTradeRow(index, 'instrument_type', e.target.value.toUpperCase())} style={manualFieldInputStyle}>
                        <option value="STK">Aktie (STK)</option>
                        <option value="OPTION">Option</option>
                        <option value="CASH">Cash</option>
                      </select>
                    ))}
                    {renderManualField('Lifecycle', 'Ob der Fill eher eine Eröffnung, Schließung oder automatische Zuordnung sein soll.', (
                      <select value={row.lifecycle} onChange={e => updateManualTradeRow(index, 'lifecycle', e.target.value)} style={manualFieldInputStyle}>
                        <option value="AUTO">Automatisch</option>
                        <option value="OPEN">Eröffnung</option>
                        <option value="CLOSE">Schließung</option>
                      </select>
                    ))}
                    {renderManualField('Datum', 'Handelsdatum dieses Fills.', (
                      <input type="date" value={row.trade_date} onChange={e => updateManualTradeRow(index, 'trade_date', e.target.value)} style={manualFieldInputStyle} />
                    ))}
                    {renderManualField('Zeit', 'Handelszeit im Format HH:MM:SS.', (
                      <input value={row.time} onChange={e => updateManualTradeRow(index, 'time', e.target.value)} placeholder="HH:MM:SS" style={manualFieldInputStyle} />
                    ))}
                    {renderManualField('Size', 'Stückzahl oder Kontraktanzahl dieses Fills.', (
                      <input value={row.size} onChange={e => updateManualTradeRow(index, 'size', e.target.value)} placeholder="1" type="number" step="0.0001" style={manualFieldInputStyle} />
                    ))}
                    {renderManualField('Preis', 'Preis pro Aktie, Kontrakt oder Einheit in der Handelswährung.', (
                      <input value={row.price} onChange={e => updateManualTradeRow(index, 'price', e.target.value)} placeholder="0.00" type="number" step="0.0001" style={manualFieldInputStyle} />
                    ))}
                    {renderManualField('Währung', 'Handelswährung des Fills, zum Beispiel USD oder EUR.', (
                      <input value={row.currency} onChange={e => updateManualTradeRow(index, 'currency', e.target.value.toUpperCase())} placeholder="USD" style={manualFieldInputStyle} />
                    ))}
                  </div>
                  {row.instrument_type === 'OPTION' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
                      {renderManualField('Basiswert', 'Der Underlying der Option, zum Beispiel SPY oder DAX.', (
                        <input value={row.underlying_symbol} onChange={e => updateManualTradeRow(index, 'underlying_symbol', e.target.value.toUpperCase())} placeholder="z. B. SPY" style={manualFieldInputStyle} />
                      ))}
                      {renderManualField('Seite', 'Ob der Leg ein Call oder Put ist.', (
                        <select value={row.put_call} onChange={e => updateManualTradeRow(index, 'put_call', e.target.value)} style={manualFieldInputStyle}>
                          <option value="C">Call</option>
                          <option value="P">Put</option>
                        </select>
                      ))}
                      {renderManualField('Verfall', 'Expiry der Option.', (
                        <input type="date" value={row.expire} onChange={e => updateManualTradeRow(index, 'expire', e.target.value)} style={manualFieldInputStyle} />
                      ))}
                      {renderManualField('Strike', 'Strike der Option.', (
                        <input value={row.strike} onChange={e => updateManualTradeRow(index, 'strike', e.target.value)} placeholder="0.00" type="number" step="0.01" style={manualFieldInputStyle} />
                      ))}
                      {renderManualField('Multiplier', 'Optionaler Kontrakt-Multiplikator, falls er nicht aus Symbol und Typ ableitbar ist.', (
                        <input value={row.multiplier} onChange={e => updateManualTradeRow(index, 'multiplier', e.target.value)} placeholder="optional" type="number" step="0.0001" style={manualFieldInputStyle} />
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
                    {renderManualField('Kommission', 'Kommission dieses einzelnen Fills in Handelswährung.', (
                      <input value={row.commission} onChange={e => updateManualTradeRow(index, 'commission', e.target.value)} placeholder="0.00" type="number" step="0.0001" style={manualFieldInputStyle} />
                    ))}
                    {renderManualField('Trade Type', 'Interner Handels-Typ. Standard ist ExchTrade.', (
                      <input value={row.trade_type} onChange={e => updateManualTradeRow(index, 'trade_type', e.target.value)} placeholder="ExchTrade" style={manualFieldInputStyle} />
                    ))}
                    {renderManualField('Notes', 'Kurze interne Kennzeichnung oder Broker-Notiz.', (
                      <input value={row.notes} onChange={e => updateManualTradeRow(index, 'notes', e.target.value)} placeholder="optional" style={manualFieldInputStyle} />
                    ))}
                    {renderManualField('Beschreibung', 'Freier Text für eine verständliche Beschreibung des manuellen Fills.', (
                      <input value={row.description} onChange={e => updateManualTradeRow(index, 'description', e.target.value)} placeholder="optional" style={manualFieldInputStyle} />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {filterPanelOpen && (
      <Card style={{ marginBottom: 0, padding: '14px 18px' }}>
        {/* Presets */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
          {PRESETS.map(p => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p.key)}
              style={{
                background: activePreset === p.key ? COLORS.cyan + '22' : '#ffffff08',
                border: `1px solid ${activePreset === p.key ? COLORS.cyan : COLORS.border}`,
                borderRadius: '6px',
                color: activePreset === p.key ? COLORS.cyan : COLORS.muted,
                padding: '4px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: activePreset === p.key ? 600 : 400,
                transition: 'all 0.15s',
              }}
            >
              {p.label}
            </button>
          ))}
          {hasFilter && (
            <button
              type="button"
              onClick={clearFilter}
              style={{
                background: 'transparent',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                color: COLORS.muted,
                padding: '4px 10px',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              ✕ Alle
            </button>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', color: COLORS.muted }}>Datumsbezug</span>
          <select
            value={dateFilterBasis}
            onChange={e => setDateFilterBasis(e.target.value)}
            style={{ ...inputStyle, minWidth: '150px' }}
          >
            <option value="close">Close Date</option>
            <option value="open">Open Date</option>
          </select>
          <span style={{ fontSize: '12px', color: COLORS.muted }}>
            Close Date: Ergebnisse am jeweiligen Schließungstag. Open Date: Ergebnisse nach Eröffnungsdatum der Gruppe.
          </span>
        </div>

        {/* Manual date inputs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', color: COLORS.muted }}>Von</span>
          <input
            type="date"
            value={filterFrom}
            onChange={e => { setFilterFrom(e.target.value); setActivePreset(null); }}
            style={inputStyle}
          />
          <span style={{ fontSize: '12px', color: COLORS.muted }}>Bis</span>
          <input
            type="date"
            value={filterTo}
            onChange={e => { setFilterTo(e.target.value); setActivePreset(null); }}
            style={inputStyle}
          />
          <span style={{ fontSize: '12px', color: COLORS.muted }}>Status</span>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={inputStyle}
          >
            <option value="ALL">Alle</option>
            <option value="OPEN">Open</option>
            <option value="WIN">Win</option>
            <option value="LOSS">Loss</option>
            <option value="BREAKEVEN">Breakeven</option>
            <option value="ASSIGNED">Assigned</option>
          </select>
          <span style={{ fontSize: '12px', color: COLORS.muted }}>Typ</span>
          <select
            value={instrumentFilter}
            onChange={e => setInstrumentFilter(e.target.value)}
            style={inputStyle}
          >
            <option value="ALL">Alle</option>
            <option value="OPTION">Nur Optionen</option>
            <option value="SPREAD">Nur Spreads</option>
            <option value="STOCK">Nur Aktien</option>
            <option value="FX_CASH">Nur FX/Cash</option>
            <option value="OTHER">Sonstige</option>
          </select>
          <span style={{ fontSize: '12px', color: COLORS.muted }}>Setup</span>
          <select
            value={setupFilter}
            onChange={e => setSetupFilter(e.target.value)}
            style={{ ...inputStyle, minWidth: '170px' }}
          >
            <option value="ALL">Alle</option>
            <option value="NONE">Ohne Setup</option>
            {(tradeJournalSetups || []).map(setup => (
              <option key={setup.id} value={String(setup.id)}>{setup.name}</option>
            ))}
          </select>
          <span style={{ fontSize: '12px', color: COLORS.muted }}>Symbol</span>
          <input
            type="text"
            value={symbolFilter}
            onChange={e => setSymbolFilter(e.target.value)}
            placeholder="z. B. SPX, DAX oder SPX DAX QQQ"
            style={{ ...inputStyle, minWidth: '130px' }}
          />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: COLORS.muted }}>
            <input
              type="checkbox"
              checked={exactSymbolFilter}
              onChange={e => setExactSymbolFilter(e.target.checked)}
            />
            Exakt
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: COLORS.muted }}>
            <input
              type="checkbox"
              checked={showPaperTrades}
              onChange={e => setShowPaperTrades(e.target.checked)}
            />
            Paper anzeigen
          </label>
          {hasFilter && (
            <span style={{ fontSize: '12px', color: COLORS.muted }}>
              — {cashStats.totalCount || 0} Konto-Bewegungen · {stats.filteredGroupCount} Trades
            </span>
          )}
        </div>
      </Card>
      )}

      {setupPanelOpen && (
        <Card style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>Setups</h3>
            <span style={{ color: COLORS.muted, fontSize: '12px' }}>{(tradeJournalSetups || []).length} Vorlagen</span>
          </div>
            <form onSubmit={submitSetupForm} style={{ display: 'grid', gap: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) minmax(260px, 1fr) auto auto', gap: '10px', alignItems: 'end' }}>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span style={{ fontSize: '12px', color: COLORS.muted }}>Name</span>
                  <input
                    type="text"
                    value={setupName}
                    onChange={e => setSetupName(e.target.value)}
                    placeholder="z. B. Morning Breakout"
                    style={inputStyle}
                    maxLength={80}
                  />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span style={{ fontSize: '12px', color: COLORS.muted }}>Beschreibung</span>
                  <input
                    type="text"
                    value={setupDescription}
                    onChange={e => setSetupDescription(e.target.value)}
                    placeholder="Optionaler Hinweis zum Setup"
                    style={inputStyle}
                    maxLength={300}
                  />
                </label>
                <Btn type="submit" color="green" disabled={setupSaving || !setupName.trim()}>
                  {setupSaving ? 'Speichert…' : (editingSetupId ? 'Setup speichern' : 'Setup anlegen')}
                </Btn>
                <Btn type="button" color="ghost" onClick={resetSetupForm} disabled={setupSaving || (!editingSetupId && !setupName && !setupDescription)}>
                  Zurücksetzen
                </Btn>
              </div>
            </form>

            {(tradeJournalSetups || []).length === 0 ? (
              <div style={{ color: COLORS.muted, fontSize: '14px' }}>Noch keine Setups angelegt.</div>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {(tradeJournalSetups || []).map(setup => (
                  <div key={setup.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '12px 14px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#ffffff04' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700 }}>{setup.name}</div>
                      <div style={{ fontSize: '12px', color: COLORS.muted, marginTop: '2px' }}>{setup.description || 'Keine Beschreibung'}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <Btn type="button" color="ghost" onClick={() => startSetupEdit(setup)} disabled={setupSaving}>Bearbeiten</Btn>
                      <Btn type="button" color="ghost" onClick={() => removeSetup(setup)} disabled={setupSaving}>Löschen</Btn>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </Card>
      )}

      {cashPanelOpen && (
        <Card style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>Konto-Bewegungen</h3>
            <span style={{ color: COLORS.muted, fontSize: '12px' }}>{cashStats.totalCount || 0} Einträge</span>
          </div>

          {!tradeJournalCashTransactionsLoaded ? (
          <div style={{ color: COLORS.muted, fontSize: '14px' }}>
            Konto-Bewegungen werden geladen…
          </div>
        ) : filteredCashTransactions.length === 0 ? (
          <div style={{ color: COLORS.muted, fontSize: '14px' }}>
            {hasFilter ? 'Keine Einträge im gewählten Zeitraum.' : 'Noch keine Konto-Bewegungen importiert.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '8px' }}>
            {cashTypes.map(type => {
              const entries = cashByType[type] || [];
              const typeStat = cashStats.byType[type] || {};
              const totalBase = typeStat.totalBase ?? 0;
              const totalRaw = typeStat.totalRaw ?? 0;
              const hasBase = (typeStat.countWithBase ?? 0) > 0;
              const hasFullTypeBaseConversion = entries.length > 0 && (typeStat.countWithBase ?? 0) === entries.length;
              const displayTotal = hasBase ? totalBase : null;
              const displayCurrency = hasBase ? displayBaseCurrency : '';
              const isTypeOpen = cashTxnOpenTypes.includes(type);
              return (
                <div key={type} style={{ border: `1px solid ${isTypeOpen ? COLORS.accent : COLORS.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => toggleCashTxnType(type)}
                    style={{
                      width: '100%', background: isTypeOpen ? '#19b7b410' : '#ffffff04',
                      border: 'none', padding: '10px 14px', cursor: 'pointer',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      color: COLORS.text, font: 'inherit',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '13px', color: isTypeOpen ? COLORS.cyan : COLORS.muted }}>{isTypeOpen ? '▾' : '▸'}</span>
                      <span style={{ fontWeight: 600, fontSize: '14px' }}>{type}</span>
                      <span style={{ fontSize: '12px', color: COLORS.muted }}>{entries.length}</span>
                      {!hasFullTypeBaseConversion && (
                        <span style={{ fontSize: '11px', color: COLORS.muted }}>EUR teilw.</span>
                      )}
                    </div>
                    <span style={{ fontWeight: 700, fontSize: '14px', color: valueColor(displayTotal) }}>
                      {formatMoneyValue(displayTotal, displayCurrency)}
                    </span>
                  </button>
                  {isTypeOpen && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#ffffff06' }}>
                          <tr>
                            <th style={{ ...th, width: '120px' }}>Datum</th>
                            <th style={th}>Beschreibung</th>
                            <th style={{ ...th, width: '80px' }}>Symbol</th>
                            <th style={{ ...th, width: '120px', textAlign: 'right' }}>Betrag</th>
                            <th style={{ ...th, width: '55px' }}>Währ.</th>
                            <th style={{ ...th, width: '120px', textAlign: 'right' }}>EUR Betrag</th>
                            <th style={{ ...th, width: '90px', textAlign: 'right' }}>FX Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.map(t => (
                            <tr key={t.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                              <td style={td}>{formatDateValue(t.trade_date)}</td>
                              <td style={{ ...td, color: COLORS.muted, fontSize: '12px' }}>{t.description || '—'}</td>
                              <td style={td}>{t.symbol || '—'}</td>
                              <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: valueColor(t.amount ?? 0) }}>
                                {formatMoneyValue(t.amount, '')}
                              </td>
                              <td style={{ ...td, color: COLORS.muted }}>{t.currency || '—'}</td>
                              <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: valueColor(t.amount_base ?? t.amount ?? 0) }}>
                                {t.amount_base != null
                                  ? formatMoneyValue(t.amount_base, displayBaseCurrency)
                                  : (t.currency === displayBaseCurrency ? formatMoneyValue(t.amount, displayBaseCurrency) : '—')}
                              </td>
                              <td style={{ ...td, textAlign: 'right', color: COLORS.muted, fontSize: '12px' }}>
                                {t.fx_rate_to_base != null ? t.fx_rate_to_base.toFixed(5) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ borderTop: `1px solid ${COLORS.border}`, background: '#ffffff05' }}>
                            <td colSpan={3} style={{ ...td, color: COLORS.muted, fontSize: '12px', fontStyle: 'italic' }}>Gesamt</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: valueColor(totalRaw) }}>
                              {formatMoneyValue(totalRaw, '')}
                            </td>
                            <td style={td} />
                            <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: valueColor(totalBase) }}>
                              {hasBase ? formatMoneyValue(totalBase, displayBaseCurrency) : '—'}
                            </td>
                            <td style={td} />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
            {/* Grand total row */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', padding: '8px 14px', borderTop: `1px solid ${COLORS.border}`, fontSize: '13px' }}>
              <span style={{ color: COLORS.muted }}>Cash-Bewegungen kumuliert ({displayBaseCurrency})</span>
              <span style={{ fontWeight: 700, color: valueColor(cashStats.accountingTotalBase) }}>
                {formatMoneyValue(cashStats.accountingTotalBase, displayBaseCurrency)}
              </span>
            </div>
          </div>
        )}
      </Card>
      )}

      {setupAnalyticsPanelOpen && (tradeJournalSetups || []).length > 0 && (
        <Card style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>Setup-Auswertung</h3>
            <span style={{ color: COLORS.muted, fontSize: '12px' }}>{setupAnalytics.rows.length} Setups mit Treffern im aktuellen Filter</span>
          </div>
          {setupAnalytics.missingBaseCount > 0 && (
            <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '12px' }}>
              {setupAnalytics.missingBaseCount} Setup-Zuordnungen ohne EUR-Umrechnung sind in den P&L-Summen nicht enthalten.
            </div>
          )}

          {setupAnalytics.rows.length === 0 ? (
            <div style={{ color: COLORS.muted, fontSize: '14px' }}>Im aktuellen Filter sind noch keine Trades einem Setup zugeordnet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: '980px', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#ffffff06' }}>
                  <tr>
                    <th style={th}>Setup</th>
                    <th style={{ ...th, width: '90px', textAlign: 'right' }}>Trades</th>
                    <th style={{ ...th, width: '90px', textAlign: 'right' }}>Closed</th>
                    <th style={{ ...th, width: '90px', textAlign: 'right' }}>Open</th>
                    <th style={{ ...th, width: '90px', textAlign: 'right' }}>Wins</th>
                    <th style={{ ...th, width: '90px', textAlign: 'right' }}>Losses</th>
                    <th style={{ ...th, width: '90px', textAlign: 'right' }}>Assigned</th>
                    <th style={{ ...th, width: '110px', textAlign: 'right' }}>Win Rate</th>
                    <th style={{ ...th, width: '130px', textAlign: 'right' }}>Net P&L ({displayBaseCurrency})</th>
                    <th style={{ ...th, width: '130px', textAlign: 'right' }}>Commissions ({displayBaseCurrency})</th>
                  </tr>
                </thead>
                <tbody>
                  {setupAnalytics.rows.map(entry => (
                    <tr key={entry.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={td}>
                        <div style={{ fontWeight: 700 }}>{entry.name}</div>
                        <div style={{ fontSize: '12px', color: COLORS.muted }}>{entry.description || '—'}</div>
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>{entry.trades}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{entry.closed}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{entry.open}</td>
                      <td style={{ ...td, textAlign: 'right', color: COLORS.green }}>{entry.wins}</td>
                      <td style={{ ...td, textAlign: 'right', color: COLORS.red }}>{entry.losses}</td>
                      <td style={{ ...td, textAlign: 'right', color: COLORS.cyan }}>{entry.assigned}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{entry.winRate == null ? '—' : formatPercentValue(entry.winRate)}</td>
                      <td style={{ ...td, textAlign: 'right', color: valueColor(entry.netPnl), fontWeight: 700 }}>{formatMoneyValue(entry.netPnl, displayBaseCurrency)}</td>
                      <td style={{ ...td, textAlign: 'right', color: entry.commissions > 0 ? COLORS.red : COLORS.text }}>{formatMoneyValue(-entry.commissions, displayBaseCurrency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <Card style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { key: 'month', label: 'Pro Monat' },
              { key: 'year', label: 'Pro Jahr' },
              { key: 'setup', label: 'Pro Setup' },
            ].map(option => (
              <button
                key={option.key}
                type="button"
                onClick={() => setPnlChartMode(option.key)}
                style={{
                  background: pnlChartMode === option.key ? `${COLORS.cyan}22` : '#ffffff08',
                  border: `1px solid ${pnlChartMode === option.key ? COLORS.cyan : COLORS.border}`,
                  borderRadius: '6px',
                  color: pnlChartMode === option.key ? COLORS.cyan : COLORS.muted,
                  padding: '4px 10px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: pnlChartMode === option.key ? 600 : 400,
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <PnlComboChart
          data={pnlChart.rows}
          COLORS={COLORS}
          mode={pnlChartMode}
          showCumulativeLine={showCumulativeLine}
          onToggleCumulativeLine={() => setShowCumulativeLine(value => !value)}
          title={
            pnlChartMode === 'setup'
              ? `P/L nach Setup (${displayBaseCurrency})`
              : pnlChartMode === 'year'
                ? `P/L pro Jahr (${displayBaseCurrency})`
                : `P/L pro Monat (${displayBaseCurrency})`
          }
          subtitle={`Die Grafik respektiert die aktuelle Quellen-, Datums-, Status-, Typ-, Setup- und Symbol-Filterung.${pnlChart.skippedGroups > 0 ? ` ${pnlChart.skippedGroups} Trades ohne EUR-Umrechnung sind nicht enthalten.` : ''}`}
        />
      </Card>

      {/* Trade Groups */}
      <Card style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>Trades</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ color: COLORS.muted, fontSize: '12px' }}>
              {visibleGroups.length} von {displayedGroupTotal} Gruppen
            </span>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[
                { key: '50', label: 'Zeige 50' },
                { key: '100', label: 'Zeige 100' },
              ].map(option => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setTradeListMode(option.key);
                    setTradePageOffset(0);
                  }}
                  style={{
                    background: tradeListMode === option.key ? `${COLORS.cyan}22` : '#ffffff08',
                    border: `1px solid ${tradeListMode === option.key ? COLORS.cyan : COLORS.border}`,
                    borderRadius: '6px',
                    color: tradeListMode === option.key ? COLORS.cyan : COLORS.muted,
                    padding: '4px 10px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: tradeListMode === option.key ? 600 : 400,
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {groupActionError && <div style={{ fontSize: '13px', color: COLORS.red, marginBottom: '10px' }}>⚠ {groupActionError}</div>}
        {groupActionMessage && <div style={{ fontSize: '13px', color: COLORS.green, marginBottom: '10px' }}>{groupActionMessage}</div>}

        {livePnlExternalTrades.length > 0 && (
          <details style={{ marginBottom: '14px' }}>
            <summary style={{ cursor: 'pointer', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
              Noch nicht synchronisierte TWS-Trades ({livePnlExternalTrades.length})
            </summary>
            <div
              style={{
                marginTop: '10px',
                padding: '12px 14px',
                borderRadius: '12px',
                border: `1px solid ${COLORS.border}`,
                background: '#ffffff05',
              }}
            >
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr style={{ background: '#ffffff05' }}>
                      <th style={{ ...th, width: '84px' }}>Status</th>
                      <th style={{ ...th, width: '88px' }}>Open</th>
                      <th style={{ ...th, minWidth: '220px' }}>Symbol</th>
                      <th style={{ ...th, width: '68px', textAlign: 'right' }}>Entry</th>
                      <th style={{ ...th, width: '58px', textAlign: 'right' }}>Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {livePnlExternalTrades.map((item) => {
                      const isExpanded = openExternalTradeKeys.includes(item.key);
                      const label = item?.symbol || 'Unbekannt';
                      const action = String(item?.action || '?').toUpperCase();
                      const quantity = item?.quantity != null ? formatCompactNumberValue(item.quantity, 0) : '—';
                      const hint = item?.message || 'Noch nicht synchronisiert.';
                      const openLabel = formatExternalTradeClock(item?.trade_time);
                      const entryLabel = item?.price != null ? formatMoneyValue(item.price, item?.currency || '') : '—';
                      return (
                        <Fragment key={item.key}>
                          <tr style={{ background: '#ffffff03' }}>
                            <td style={{ ...td, ...compactMainCell, borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}`, borderLeft: `1px solid ${COLORS.border}`, borderTopLeftRadius: '12px', borderBottomLeftRadius: isExpanded ? 0 : '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                  type="button"
                                  onClick={() => setOpenExternalTradeKeys((prev) => (
                                    prev.includes(item.key) ? prev.filter((value) => value !== item.key) : [...prev, item.key]
                                  ))}
                                  style={{ background: 'transparent', border: 'none', color: COLORS.muted, cursor: 'pointer', fontSize: '16px', padding: 0, lineHeight: 1 }}
                                >
                                  {isExpanded ? '▾' : '▸'}
                                </button>
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '82px',
                                    padding: '2px 8px',
                                    borderRadius: '999px',
                                    border: '1px solid #64748b',
                                    background: 'rgba(148,163,184,0.12)',
                                    color: '#cbd5e1',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    letterSpacing: '0.04em',
                                  }}
                                >
                                  Neu in TWS
                                </span>
                              </div>
                            </td>
                            <td style={{ ...td, ...compactMainCell, borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}` }}>
                              {openLabel}
                            </td>
                            <td
                              style={{
                                ...td,
                                ...compactMainCell,
                                borderTop: `1px solid ${COLORS.border}`,
                                borderBottom: `1px solid ${COLORS.border}`,
                                color: COLORS.cyan,
                                fontWeight: 700,
                                maxWidth: '280px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              title={label}
                            >
                              <div>{label}</div>
                              <div style={{ marginTop: '4px', fontSize: '11px', color: COLORS.muted, fontWeight: 500 }}>
                                {action} · {item?.security_type || '—'} · {item?.exchange || '—'} · {item?.legs?.length || 0} Legs
                              </div>
                            </td>
                            <td style={{ ...td, ...compactMainCell, borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}`, textAlign: 'right' }}>
                              {entryLabel}
                            </td>
                            <td style={{ ...td, ...compactMainCell, borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}`, textAlign: 'right', color: action === 'BOT' ? COLORS.green : (action === 'SLD' ? COLORS.red : COLORS.text), fontWeight: 700 }}>
                              {quantity}
                            </td>
                            <td
                              title={hint}
                              style={{
                                ...td,
                                ...compactMainCell,
                                borderTop: `1px solid ${COLORS.border}`,
                                borderBottom: `1px solid ${COLORS.border}`,
                                borderRight: `1px solid ${COLORS.border}`,
                                borderTopRightRadius: '12px',
                                borderBottomRightRadius: isExpanded ? 0 : '12px',
                                color: COLORS.muted,
                              }}
                            >
                              {item?.exchange || '—'}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={5} style={{ padding: '0 16px 16px', background: 'rgba(148,163,184,0.08)', borderLeft: `1px solid ${COLORS.border}`, borderRight: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}`, borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
                                <div style={{ marginTop: '12px' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead style={{ background: '#ffffff08' }}>
                                      <tr>
                                        <th style={th}>Leg</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Aktion</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Qty</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Preis</th>
                                        <th style={th}>Typ</th>
                                        <th style={th}>Börse</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(item.legs || []).map((leg, legIndex) => (
                                        <tr key={`${item.key}-leg-${legIndex}`}>
                                          <td style={td}>{leg?.local_symbol || leg?.symbol || '—'}</td>
                                          <td style={{ ...td, textAlign: 'right', color: String(leg?.action || '').toUpperCase() === 'BOT' ? COLORS.green : (String(leg?.action || '').toUpperCase() === 'SLD' ? COLORS.red : COLORS.text), fontWeight: 700 }}>
                                            {leg?.action || '—'}
                                          </td>
                                          <td style={{ ...td, textAlign: 'right' }}>{leg?.quantity != null ? formatCompactNumberValue(leg.quantity, 0) : '—'}</td>
                                          <td style={{ ...td, textAlign: 'right' }}>{leg?.price != null ? formatMoneyValue(leg.price, leg?.currency || '') : '—'}</td>
                                          <td style={td}>{leg?.security_type || '—'}</td>
                                          <td style={td}>{leg?.exchange || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </details>
        )}

        {selectedGroupKeys.length > 0 && (
          <div style={{ display: 'grid', gap: '10px', marginBottom: '14px', padding: '12px 14px', border: `1px solid ${COLORS.cyan}`, borderRadius: '12px', background: `${COLORS.cyan}10` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '13px' }}>Mehrfachzuweisung für {selectedGroupKeys.length} Trades</div>
                <div style={{ fontSize: '12px', color: COLORS.muted }}>
                  Wähle ein oder mehrere Setups und speichere sie für alle markierten Trades.
                </div>
              </div>
              <Btn type="button" color="ghost" onClick={() => setSelectedGroupKeys([])} disabled={bulkAssignSaving}>
                Auswahl löschen
              </Btn>
            </div>
            {(tradeJournalSetups || []).length === 0 ? (
              <div style={{ fontSize: '12px', color: COLORS.muted }}>Lege zuerst ein Setup an.</div>
            ) : (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {(tradeJournalSetups || []).map(setup => {
                  const checked = bulkSetupIds.includes(setup.id);
                  return (
                    <label
                      key={setup.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        borderRadius: '999px',
                        border: `1px solid ${checked ? COLORS.cyan : COLORS.border}`,
                        background: checked ? `${COLORS.cyan}18` : '#ffffff05',
                        color: checked ? COLORS.cyan : COLORS.text,
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleBulkSetupId(setup.id)}
                      />
                      <span>{setup.name}</span>
                    </label>
                  );
                })}
                <Btn type="button" color="green" onClick={saveBulkSetups} disabled={bulkAssignSaving}>
                  {bulkAssignSaving ? 'Speichert…' : 'Setups für Auswahl speichern'}
                </Btn>
              </div>
            )}
          </div>
        )}

        {displayedGroupTotal === 0 ? (
          <div style={{ color: COLORS.muted, fontSize: '14px' }}>
            {hasFilter ? 'Keine Trades im gewählten Zeitraum.' : 'Noch keine importierten Trades vorhanden.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', paddingBottom: '4px' }}>
            <table style={{ width: '100%', minWidth: '1240px', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr style={{ background: '#ffffff05' }}>
                  <th style={{ ...th, width: '36px' }} />
                  <th style={{ ...th, width: '40px', textAlign: 'center' }}>
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAllVisibleGroups} />
                  </th>
                  <th style={{ ...th, width: '84px' }}>{renderHeaderButton('Status', 'status')}</th>
                  <th style={{ ...th, width: '88px' }}>{renderHeaderButton('Open', 'open_date')}</th>
                  <th style={{ ...th, minWidth: '150px' }}>{renderHeaderButton('Symbol', 'symbol')}</th>
                  <th style={{ ...th, width: '68px', textAlign: 'right' }}>{renderHeaderButton('Entry', 'entry', 'right')}</th>
                  <th style={{ ...th, width: '68px', textAlign: 'right' }}>{renderHeaderButton('Exit', 'exit', 'right')}</th>
                  <th style={{ ...th, width: '58px', textAlign: 'right' }}>{renderHeaderButton('Size', 'size', 'right')}</th>
                  <th style={{ ...th, width: '112px', textAlign: 'right' }}>{renderHeaderButton(`P/L ${displayBaseCurrency}`, 'return_amount', 'right')}</th>
                  <th style={{ ...th, width: '112px', textAlign: 'right' }}>Live P/L {displayBaseCurrency}</th>
                  <th style={{ ...th, width: '116px', textAlign: 'right' }}>Marktwert {displayBaseCurrency}</th>
                  <th style={{ ...th, width: '84px', textAlign: 'right' }}>{renderHeaderButton('Return %', 'return_percent', 'right')}</th>
                  <th style={{ ...th, minWidth: '108px' }}>Setups</th>
                  <th style={{ ...th, width: '88px' }}>{renderHeaderButton('Close', 'close_date')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleGroups.map(group => {
                  const isOpen = openGroupIds.includes(group.id);
                  const liveRow = livePnlByGroupKey.get(group.group_key);
                  const pendingHintText = String(liveRow?.message || '');
                  const isPendingSync = Boolean(
                    liveRow?.pending_sync ||
                    /IBKR-Sync|TWS\/App noch nicht synchron|TWS weicht bereits von der App ab/i.test(pendingHintText)
                  );
                  const pendingSyncText = liveRow?.message || 'Noch nicht synchronisiert.';
                  const optionRisk = calculateOpenOptionRisk(group, displayBaseCurrency);
                  const optionProfileMode = optionProfileModes[group.group_key] === 'today' ? 'today' : 'expiry';
                  const optionProfileSupportsToday = liveRow?.current_underlying_price != null && liveRow?.live_pnl_base != null;
                  const optionProfile = optionRisk
                    ? buildOptionProfile(
                        group,
                        displayBaseCurrency,
                        liveRow?.current_underlying_price ?? null,
                        liveRow?.live_pnl_base ?? null,
                        optionProfileSupportsToday ? optionProfileMode : 'expiry',
                      )
                    : null;
                  const optionStrategyName = optionProfile ? detectOptionStrategy(group, optionProfile) : null;
                  const optionBreakEvenDistance = optionProfile ? buildBreakEvenDistance(optionProfile) : null;
                  const scenarioPrice = optionProfile
                    ? Number(optionProfileScenarioPrices[group.group_key] ?? optionProfile.currentUnderlyingPrice ?? optionProfile.minPrice)
                    : null;
                  const scenarioPnl = optionProfile ? interpolateProfilePnl(optionProfile.points, scenarioPrice) : null;
                  const optionGreeks = optionProfile ? calculateApproxGreeks(optionProfile, liveRow) : null;
                  const isEditingGroup = editingGroupKey === group.group_key;
                  const editDraft = groupEditDrafts[group.group_key];
                  const borderColor = isPendingSync
                    ? '#64748b'
                    : (isOpen ? COLORS.accent : COLORS.border);
                  const rowBg = isPendingSync
                    ? (isOpen ? 'rgba(148,163,184,0.10)' : 'rgba(148,163,184,0.07)')
                    : (isOpen ? '#19b7b41a' : '#ffffff04');
                  const pendingAccentStyle = isPendingSync
                    ? {
                        boxShadow: 'inset 3px 0 0 rgba(148,163,184,0.55)',
                      }
                    : null;
                  const cellBorder = { borderTop: `1px solid ${borderColor}`, borderBottom: `1px solid ${borderColor}` };
                  return (
                    <Fragment key={group.id}>
                      <tr style={{ backgroundColor: rowBg }}>
                        <td style={{ ...td, ...compactMainCell, ...cellBorder, ...pendingAccentStyle, width: '36px', textAlign: 'center', borderLeft: `1px solid ${borderColor}`, borderTopLeftRadius: '14px', borderBottomLeftRadius: isOpen ? 0 : '14px', paddingLeft: '10px', paddingRight: '10px' }}>
                          <button type="button" onClick={() => toggleGroup(group.id)} style={{ background: 'transparent', border: 'none', color: COLORS.muted, cursor: 'pointer', fontSize: '16px', padding: 0, lineHeight: 1 }}>
                            {isOpen ? '▾' : '▸'}
                          </button>
                        </td>
                        <td style={{ ...td, ...compactMainCell, ...cellBorder, width: '40px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedGroupKeys.includes(group.group_key)}
                            onChange={() => toggleGroupSelection(group.group_key)}
                          />
                        </td>
                        <td style={{ ...td, ...compactMainCell, ...cellBorder }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '64px', padding: '2px 8px', borderRadius: '999px', border: `1px solid ${
                            group.status === 'WIN' ? '#14532d' :
                            group.status === 'LOSS' ? '#7f1d1d' :
                            group.status === 'OPEN' ? '#7c5a12' :
                            group.status === 'ASSIGNED' ? '#1d4ed8' :
                            COLORS.border
                          }`, background:
                            group.status === 'WIN' ? '#14532d22' :
                            group.status === 'LOSS' ? '#7f1d1d22' :
                            group.status === 'OPEN' ? '#7c5a1222' :
                            group.status === 'ASSIGNED' ? '#1d4ed822' :
                            '#ffffff08', color:
                            group.status === 'WIN' ? '#22c55e' :
                            group.status === 'LOSS' ? '#ef4444' :
                            group.status === 'OPEN' ? '#f59e0b' :
                            group.status === 'ASSIGNED' ? '#60a5fa' :
                            COLORS.muted, fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em' }}>
                            {group.status}
                          </span>
                          {isPendingSync && (
                            <div style={{ marginTop: '6px' }}>
                              <span
                                title={pendingSyncText}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: '92px',
                                  padding: '3px 10px',
                                  borderRadius: '999px',
                                  border: '1px solid #64748b',
                                  background: 'rgba(148,163,184,0.12)',
                                  color: '#cbd5e1',
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  letterSpacing: '0.04em',
                                  boxShadow: '0 0 0 1px rgba(148,163,184,0.06)',
                                }}
                              >
                                In Schwebe
                              </span>
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, ...compactMainCell, ...cellBorder }}>{formatDateCompactValue(group.open_date)}</td>
                        <td style={{ ...td, ...compactMainCell, ...cellBorder, fontWeight: 700, color: isPendingSync ? '#cbd5e1' : COLORS.cyan, maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={group.symbol || ''}>
                          <span
                            style={
                              isPendingSync
                                ? {
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: '999px',
                                    background: 'rgba(148,163,184,0.10)',
                                    border: '1px solid rgba(148,163,184,0.28)',
                                    color: '#cbd5e1',
                                  }
                                : undefined
                            }
                          >
                            {group.symbol || '—'}
                          </span>
                          {isPendingSync && (
                            <div
                              title={pendingSyncText}
                              style={{ marginTop: '5px', fontSize: '11px', fontWeight: 500, color: '#94a3b8' }}
                            >
                              Noch nicht synchronisiert.
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, ...compactMainCell, ...cellBorder, textAlign: 'right' }}>{group.entry != null ? formatMoneyValue(group.entry, group.currency || '') : '—'}</td>
                        <td style={{ ...td, ...compactMainCell, ...cellBorder, textAlign: 'right' }}>{group.exit != null ? formatMoneyValue(group.exit, group.currency || '') : '—'}</td>
                        <td style={{ ...td, ...compactMainCell, ...cellBorder, textAlign: 'right' }}>{group.size ?? '—'}</td>
                        <td title="Realisierte P/L im gewählten Zeitraum. Einzelbuchungen und Return % beziehen sich auf die gesamte Gruppe." style={{ ...td, ...compactMainCell, ...cellBorder, textAlign: 'right', color: valueColor(journalPerformance(group).net), fontWeight: 700 }}>
                          {formatMoneyValue(journalPerformance(group).net, displayBaseCurrency)}
                        </td>
                        <td
                          style={{
                            ...td,
                            ...compactMainCell,
                            ...cellBorder,
                            textAlign: 'right',
                            color: liveRow?.priced ? (isPendingSync ? '#cbd5e1' : valueColor(liveRow.live_pnl_base)) : COLORS.muted,
                            fontWeight: 700,
                          }}
                          title={!liveRow ? 'Noch nicht geladen' : (liveRow.message || '')}
                        >
                          {group.status === 'OPEN'
                            ? (liveRow?.priced ? formatMoneyValue(liveRow.live_pnl_base, displayBaseCurrency) : '—')
                            : '—'}
                        </td>
                        <td
                          style={{
                            ...td,
                            ...compactMainCell,
                            ...cellBorder,
                            textAlign: 'right',
                            color: liveRow?.priced ? (isPendingSync ? '#cbd5e1' : valueColor(liveRow.current_close_value_base)) : COLORS.muted,
                            fontWeight: 700,
                          }}
                          title={!liveRow ? 'Noch nicht geladen' : 'Signierter Marktwert der offenen Position'}
                        >
                          {group.status === 'OPEN'
                            ? (liveRow?.priced ? formatMoneyValue(liveRow.current_close_value_base, displayBaseCurrency) : '—')
                            : '—'}
                        </td>
                        <td style={{ ...td, ...compactMainCell, ...cellBorder, textAlign: 'right', color: valueColor(group.return_percent ?? 0), fontWeight: 700 }}>{formatPercentValue(group.return_percent)}</td>
                        <td style={{ ...td, ...compactMainCell, ...cellBorder }}>
                          <details open={openGroupSetupKey === group.group_key}>
                            <summary
                              onClick={(event) => {
                                event.preventDefault();
                                setOpenGroupSetupKey(current => current === group.group_key ? '' : group.group_key);
                              }}
                              style={{ cursor: 'pointer', color: COLORS.cyan, fontSize: '10px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}
                            >
                              {Array.isArray(group.setups) && group.setups.length > 0
                                ? group.setups.map(setup => setup.name).join(', ')
                                : 'Setups wählen'}
                            </summary>
                            <div style={{ marginTop: '8px', minWidth: '220px', display: 'grid', gap: '8px', padding: '10px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#0f1d23' }}>
                              {(tradeJournalSetups || []).length === 0 ? (
                                <div style={{ fontSize: '12px', color: COLORS.muted }}>Keine Setups vorhanden.</div>
                              ) : (
                                <>
                                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                    {(tradeJournalSetups || []).map(setup => {
                                      const checked = getAssignedSetupIds(group).includes(setup.id);
                                      return (
                                        <label
                                          key={setup.id}
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '4px 8px',
                                            borderRadius: '999px',
                                            border: `1px solid ${checked ? COLORS.cyan : COLORS.border}`,
                                            background: checked ? `${COLORS.cyan}18` : '#ffffff05',
                                            color: checked ? COLORS.cyan : COLORS.text,
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleGroupSetupDraft(group.group_key, setup.id)}
                                          />
                                          <span>{setup.name}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                  <div>
                                    <Btn
                                      type="button"
                                      color="ghost"
                                      onClick={() => saveGroupSetups(group)}
                                      disabled={groupSetupSavingKey === group.group_key}
                                    >
                                      {groupSetupSavingKey === group.group_key ? 'Speichert…' : 'Speichern'}
                                    </Btn>
                                  </div>
                                </>
                              )}
                            </div>
                          </details>
                        </td>
                        <td style={{ ...td, ...compactMainCell, ...cellBorder, borderRight: `1px solid ${borderColor}`, borderTopRightRadius: '14px', borderBottomRightRadius: isOpen ? 0 : '14px' }}>
                          {formatDateCompactValue(group.close_date)}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr key={`${group.id}-details`}>
                          <td colSpan={14} style={{ padding: '0 16px 16px', background: isPendingSync ? 'rgba(148,163,184,0.08)' : '#19b7b41a', borderLeft: `1px solid ${borderColor}`, borderRight: `1px solid ${borderColor}`, borderBottom: `1px solid ${borderColor}`, borderBottomLeftRadius: '14px', borderBottomRightRadius: '14px' }}>
                            <div style={{ marginTop: '12px' }}>
                              {isPendingSync && (
                                <div
                                  style={{
                                    marginBottom: '12px',
                                    padding: '10px 12px',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(148,163,184,0.35)',
                                    background: 'rgba(148,163,184,0.10)',
                                    color: '#cbd5e1',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                  }}
                                >
                                  In Schwebe: {pendingSyncText}
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                                {isEditingGroup ? (
                                  <>
                                    <Btn type="button" color="green" onClick={() => saveGroupEdit(group)} disabled={groupEditSavingKey === group.group_key}>
                                      {groupEditSavingKey === group.group_key ? 'Speichert…' : 'Speichern'}
                                    </Btn>
                                    <Btn type="button" color="ghost" onClick={() => cancelGroupEdit(group.group_key)} disabled={groupEditSavingKey === group.group_key}>
                                      Abbrechen
                                    </Btn>
                                  </>
                                ) : (
                                  <Btn type="button" color="ghost" onClick={() => startGroupEdit(group)} disabled={groupDeleteSavingKey === group.group_key}>
                                    Bearbeiten
                                  </Btn>
                                )}
                                <Btn type="button" color="ghost" onClick={() => deleteGroup(group)} disabled={groupDeleteSavingKey === group.group_key || groupEditSavingKey === group.group_key}>
                                  {groupDeleteSavingKey === group.group_key ? 'Löscht…' : 'Löschen'}
                                </Btn>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, minmax(120px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                                <div style={{ padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#ffffff05' }}>
                                  <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Side</div>
                                  <div style={{ marginTop: '4px', fontWeight: 700 }}>{group.side || '—'}</div>
                                </div>
                                <div style={{ padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#ffffff05' }}>
                                  <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>C/P</div>
                                  <div style={{ marginTop: '4px', fontWeight: 700 }}>{group.call_put || '—'}</div>
                                </div>
                                <div style={{ padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#ffffff05' }}>
                                  <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Strategie</div>
                                  <div style={{ marginTop: '4px', fontWeight: 700, color: optionStrategyName ? COLORS.text : COLORS.muted }}>
                                    {optionStrategyName || (group.call_put === 'SPREAD' ? 'Mehrbeiniger Trade' : 'Einzel-Leg')}
                                  </div>
                                </div>
                                <div style={{ padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#ffffff05' }}>
                                  <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Hold</div>
                                  <div style={{ marginTop: '4px', fontWeight: 700 }}>
                                    {group.status === 'OPEN' ? formatOpenTradeAge(group.open_date) : formatHoldTime(group.hold_seconds)}
                                  </div>
                                </div>
                                <div style={{ padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#ffffff05' }}>
                                <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Commissions</div>
                                  <div style={{ marginTop: '4px', fontWeight: 700, color: group.commissions_base > 0 ? COLORS.red : COLORS.text }}>
                                    {group.commissions_base != null ? formatMoneyValue(-group.commissions_base, displayBaseCurrency) : '—'}
                                  </div>
                                </div>
                                <div style={{ padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#ffffff05' }}>
                                  <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Max Win</div>
                                  <div style={{ marginTop: '4px', fontWeight: 700, color: optionRisk?.maxWin === 'UNLIMITED' ? COLORS.green : valueColor(optionRisk?.maxWin) }}>
                                    {formatRiskValue(optionRisk?.maxWin, displayBaseCurrency)}
                                  </div>
                                </div>
                                <div style={{ padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#ffffff05' }}>
                                  <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Max Loss</div>
                                  <div style={{ marginTop: '4px', fontWeight: 700, color: optionRisk?.maxLoss === 'UNLIMITED' ? COLORS.red : (optionRisk?.maxLoss != null ? COLORS.red : COLORS.muted) }}>
                                    {formatRiskValue(optionRisk?.maxLoss, displayBaseCurrency)}
                                  </div>
                                </div>
                                <div style={{ padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#ffffff05' }}>
                                  <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Live P&L</div>
                                  <div style={{ marginTop: '4px', fontWeight: 700, color: liveRow?.priced ? (isPendingSync ? '#cbd5e1' : valueColor(liveRow.live_pnl_base)) : COLORS.muted }}>
                                    {liveRow?.priced ? formatMoneyValue(liveRow.live_pnl_base, displayBaseCurrency) : '—'}
                                  </div>
                                  {liveRow?.current_mark_price != null && (
                                    <div style={{ marginTop: '4px', fontSize: '11px', color: COLORS.muted }}>
                                      Mark: {formatMoneyValue(liveRow.current_mark_price, liveRow.mark_price_currency || group.currency || '')}
                                    </div>
                                  )}
                                  {isPendingSync && (
                                    <div style={{ marginTop: '4px', fontSize: '11px', color: '#cbd5e1' }}>
                                      In Schwebe
                                    </div>
                                  )}
                                </div>
                                <div style={{ padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#ffffff05' }}>
                                  <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Marktwert</div>
                                  <div style={{ marginTop: '4px', fontWeight: 700, color: liveRow?.priced ? (isPendingSync ? '#cbd5e1' : valueColor(liveRow.current_close_value_base)) : COLORS.muted }}>
                                    {liveRow?.priced ? formatMoneyValue(liveRow.current_close_value_base, displayBaseCurrency) : '—'}
                                  </div>
                                </div>
                              </div>
                              {Array.isArray(liveRow?.leg_marks) && liveRow.leg_marks.length > 0 && (
                                <div style={{ marginBottom: '14px', padding: '12px 14px', border: `1px solid ${COLORS.border}`, borderRadius: '12px', background: '#0f1d23' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: COLORS.text }}>Leg-Marks</div>
                                    <div style={{ fontSize: '11px', color: COLORS.muted }}>
                                      Quelle: {liveRow?.pricing_source || '—'}{liveRow?.approximate ? ' · näherungsweise' : ''}{isPendingSync ? ' · in Schwebe' : ''}
                                    </div>
                                  </div>
                                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead style={{ background: '#ffffff08' }}>
                                      <tr>
                                        <th style={th}>Leg</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Net Qty</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Multiplier</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Bid</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Ask</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Last</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Mark</th>
                                        <th style={th}>Quelle</th>
                                        <th style={{ ...th, textAlign: 'right' }}>FX</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Leg-Marktwert {displayBaseCurrency}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {liveRow.leg_marks.map((leg, index) => {
                                        const netQty = Number(leg?.net_qty || 0);
                                        const multiplier = Number(leg?.multiplier || 1);
                                        const markPrice = Number(leg?.current_mark_price || 0);
                                        const fxRate = Number(leg?.fx_rate_to_base || 1);
                                        const signedLegValueBase = netQty * multiplier * markPrice * fxRate;
                                        const legLabel = [
                                          leg?.expiry ? formatDateValue(leg.expiry) : null,
                                          leg?.strike != null ? `${leg.strike}${leg.type || ''}` : (leg?.type || null),
                                        ].filter(Boolean).join(' · ') || `Leg ${index + 1}`;
                                        return (
                                          <tr key={`${group.group_key}-leg-mark-${index}`} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                                            <td style={td}>{legLabel}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>{formatCompactNumberValue(netQty, 4)}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>{formatCompactNumberValue(multiplier, 2)}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>
                                              {leg?.bid != null ? formatMoneyValue(leg.bid, leg?.currency || group.currency || '') : '—'}
                                            </td>
                                            <td style={{ ...td, textAlign: 'right' }}>
                                              {leg?.ask != null ? formatMoneyValue(leg.ask, leg?.currency || group.currency || '') : '—'}
                                            </td>
                                            <td style={{ ...td, textAlign: 'right' }}>
                                              {leg?.last_price != null ? formatMoneyValue(leg.last_price, leg?.currency || group.currency || '') : '—'}
                                            </td>
                                            <td style={{ ...td, textAlign: 'right' }}>
                                              {Number.isFinite(markPrice) ? formatMoneyValue(markPrice, leg?.currency || group.currency || '') : '—'}
                                            </td>
                                            <td style={td}>{leg?.mark_source || '—'}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>{Number.isFinite(fxRate) ? formatCompactNumberValue(fxRate, 5) : '—'}</td>
                                            <td style={{ ...td, textAlign: 'right', color: valueColor(signedLegValueBase), fontWeight: 700 }}>
                                              {Number.isFinite(signedLegValueBase) ? formatMoneyValue(signedLegValueBase, displayBaseCurrency) : '—'}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {optionProfile && (
                                <div style={{ marginBottom: '14px', padding: '14px', border: `1px solid ${COLORS.border}`, borderRadius: '14px', background: '#0e2026' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '12px' }}>
                                    <div>
                                      <div style={{ fontSize: '16px', fontWeight: 700, color: COLORS.text }}>Optionsprofil</div>
                                      <div style={{ marginTop: '4px', fontSize: '12px', color: COLORS.muted }}>
                                        {optionProfile.mode === 'today'
                                          ? 'Heute ~ auf Basis von Live P&L und Kurs'
                                          : `Payoff bei Verfall ${optionProfile.expiry ? `(${formatDateValue(optionProfile.expiry)})` : ''}`}
                                      </div>
                                      {optionStrategyName && (
                                        <div style={{ marginTop: '8px', display: 'inline-flex', padding: '6px 10px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', fontSize: '12px', fontWeight: 700, color: COLORS.text }}>
                                          {optionStrategyName}
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                      {optionProfileSupportsToday && (
                                        <div style={{ display: 'inline-flex', border: `1px solid ${COLORS.border}`, borderRadius: '999px', overflow: 'hidden', background: '#ffffff05' }}>
                                          <button
                                            type="button"
                                            onClick={() => setOptionProfileModes(prev => ({ ...prev, [group.group_key]: 'expiry' }))}
                                            style={{
                                              border: 'none',
                                              background: optionProfileMode === 'expiry' ? `${COLORS.cyan}22` : 'transparent',
                                              color: optionProfileMode === 'expiry' ? COLORS.cyan : COLORS.muted,
                                              padding: '6px 10px',
                                              fontSize: '12px',
                                              cursor: 'pointer',
                                            }}
                                          >
                                            Verfall
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => setOptionProfileModes(prev => ({ ...prev, [group.group_key]: 'today' }))}
                                            style={{
                                              border: 'none',
                                              borderLeft: `1px solid ${COLORS.border}`,
                                              background: optionProfileMode === 'today' ? `${COLORS.cyan}22` : 'transparent',
                                              color: optionProfileMode === 'today' ? COLORS.cyan : COLORS.muted,
                                              padding: '6px 10px',
                                              fontSize: '12px',
                                              cursor: 'pointer',
                                            }}
                                            title="Heute ~ ist eine Näherung auf Basis des aktuellen Live P&L."
                                          >
                                            Heute ~
                                          </button>
                                        </div>
                                      )}
                                      <div style={{ padding: '6px 10px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', fontSize: '12px', color: COLORS.text }}>
                                        Break-even: {optionProfile.breakEvens.length > 0
                                          ? optionProfile.breakEvens.map(value => value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })).join(' / ')
                                          : '—'}
                                      </div>
                                      {optionBreakEvenDistance && (
                                        <div style={{ padding: '6px 10px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', fontSize: '12px', color: COLORS.text }}>
                                          Abstand zu BE: {optionBreakEvenDistance.absDistance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          {optionBreakEvenDistance.percent != null ? ` (${optionBreakEvenDistance.percent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)` : ''}
                                          {optionBreakEvenDistance.direction === 'auf' ? ' auf BE' : ` ${optionBreakEvenDistance.direction}`}
                                        </div>
                                      )}
                                      <div style={{ padding: '6px 10px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', fontSize: '12px', color: COLORS.text }}>
                                        Strikes: {optionProfile.strikes.map(value => value.toLocaleString('en-US', { maximumFractionDigits: 2 })).join(' / ')}
                                      </div>
                                      {liveRow?.current_underlying_price != null && (
                                        <div style={{ padding: '6px 10px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', fontSize: '12px', color: COLORS.text }}>
                                          Underlying: {formatMoneyValue(liveRow.current_underlying_price, liveRow.underlying_price_currency || group.currency || '')}
                                        </div>
                                      )}
                                      {liveRow?.priced && liveRow?.current_underlying_price == null && (
                                        <div style={{ padding: '6px 10px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', fontSize: '12px', color: COLORS.muted }}>
                                          Aktueller Kurs nicht verfügbar
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {optionProfile.unsupportedReason ? (
                                    <div style={{ fontSize: '12px', color: COLORS.muted }}>{optionProfile.unsupportedReason}</div>
                                  ) : (
                                    <>
                                      <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: '12px', background: '#09161b', padding: '10px 10px 4px', marginBottom: '12px' }}>
                                        <OptionProfileChart profile={optionProfile} currency={displayBaseCurrency} COLORS={COLORS} valueColor={valueColor} scenarioPrice={scenarioPrice} />
                                      </div>
                                      {optionProfile.currentUnderlyingPrice != null && (
                                        <div style={{ display: 'grid', gap: '8px', marginBottom: '12px', padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '12px', background: '#ffffff05' }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            <div style={{ fontSize: '12px', color: COLORS.muted }}>
                                              Szenario-Kurs: <span style={{ color: COLORS.text, fontWeight: 700 }}>{formatMoneyValue(scenarioPrice, optionProfile.priceCurrency || '')}</span>
                                            </div>
                                            <div style={{ fontSize: '12px', color: scenarioPnl != null ? valueColor(scenarioPnl) : COLORS.muted, fontWeight: 700 }}>
                                              Verfall beim Szenario: {scenarioPnl != null ? formatMoneyValue(scenarioPnl, displayBaseCurrency) : '—'}
                                            </div>
                                          </div>
                                          <input
                                            type="range"
                                            min={optionProfile.minPrice}
                                            max={optionProfile.maxPrice}
                                            step={Math.max((optionProfile.maxPrice - optionProfile.minPrice) / 200, 0.01)}
                                            value={scenarioPrice}
                                            onChange={(event) => setOptionProfileScenarioPrices((current) => ({ ...current, [group.group_key]: Number(event.target.value) }))}
                                            style={{ width: '100%' }}
                                          />
                                        </div>
                                      )}
                                      {optionGreeks && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                                          {[
                                            ['Delta ~', optionGreeks.delta],
                                            ['Gamma ~', optionGreeks.gamma],
                                            ['Theta/Tag ~', optionGreeks.theta],
                                            ['Vega ~', optionGreeks.vega],
                                          ].map(([label, value]) => (
                                            <div key={`${group.group_key}-${label}`} style={{ padding: '10px 12px', borderRadius: '10px', border: `1px solid ${COLORS.border}`, background: '#ffffff05' }}>
                                              <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                                              <div style={{ marginTop: '4px', fontWeight: 700, color: valueColor(Number(value || 0)) }}>
                                                {Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
                                        {optionProfile.legs.map((leg, index) => (
                                          <div key={`${group.group_key}-leg-${index}`} style={{ padding: '10px 12px', borderRadius: '10px', border: `1px solid ${COLORS.border}`, background: '#ffffff05' }}>
                                            <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                              Leg {index + 1}
                                            </div>
                                            <div style={{ marginTop: '4px', fontWeight: 700, color: leg.netQty > 0 ? COLORS.green : COLORS.red }}>
                                              {leg.netQty > 0 ? '+' : ''}{leg.netQty} {group.symbol?.split(' ')[0] || ''} {leg.strike}{leg.type}
                                            </div>
                                            <div style={{ marginTop: '4px', fontSize: '12px', color: COLORS.muted }}>
                                              Multiplier {getDetailMultiplier(leg).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
                                <thead style={{ background: '#ffffff08' }}>
                                  <tr>
                                    <th style={th}>Action</th>
                                    <th style={th}>Type</th>
                                    <th style={th}>O/C</th>
                                    <th style={th}>Expire</th>
                                    <th style={th}>Strike</th>
                                    <th style={th}>Date</th>
                                    <th style={th}>Time</th>
                                    <th style={th}>Size</th>
                                    <th style={th}>Pos.</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(isEditingGroup && editDraft ? editDraft.fills : group.fills).map(fill => (
                                    <tr key={fill.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                                      <td style={td}>{isEditingGroup ? <input value={fill.action ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'action', e.target.value)} style={{ ...inputStyle, width: '80px' }} /> : (fill.action || '—')}</td>
                                      <td style={td}>{isEditingGroup ? <input value={fill.instrument_type ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'instrument_type', e.target.value)} style={{ ...inputStyle, width: '80px' }} /> : (fill.instrument_type || '—')}</td>
                                      <td style={td}>{isEditingGroup ? <input value={fill.open_close_indicator ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'open_close_indicator', e.target.value)} style={{ ...inputStyle, width: '70px' }} /> : (fill.open_close_indicator || '—')}</td>
                                      <td style={td}>{isEditingGroup ? <input type="date" value={fill.expire ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'expire', e.target.value)} style={{ ...inputStyle, width: '145px' }} /> : formatDateValue(fill.expire)}</td>
                                      <td style={td}>{isEditingGroup ? <input type="number" step="0.01" value={fill.strike ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'strike', e.target.value)} style={{ ...inputStyle, width: '90px' }} /> : (fill.strike ?? '—')}</td>
                                      <td style={td}>{isEditingGroup ? <input type="date" value={fill.trade_date ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'trade_date', e.target.value)} style={{ ...inputStyle, width: '145px' }} /> : formatDateValue(fill.trade_date)}</td>
                                      <td style={td}>{isEditingGroup ? <input value={fill.time ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'time', e.target.value)} style={{ ...inputStyle, width: '90px' }} /> : (fill.time || '—')}</td>
                                      <td style={td}>{isEditingGroup ? <input type="number" step="0.01" value={fill.size ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'size', e.target.value)} style={{ ...inputStyle, width: '90px' }} /> : (fill.size ?? '—')}</td>
                                      <td style={td}>{fill.position ?? '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ background: '#ffffff08' }}>
                                  <tr>
                                    <th style={{ ...th, textAlign: 'right' }}>Price</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Return {displayBaseCurrency}</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Commission {displayBaseCurrency}</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Fees {displayBaseCurrency}</th>
                                    <th style={{ ...th, textAlign: 'right' }}>FX</th>
                                    <th style={th}>Currency</th>
                                    <th style={th}>Trade Type</th>
                                    <th style={th}>Flags</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(isEditingGroup && editDraft ? editDraft.fills : group.fills).map(fill => (
                                    <tr key={`${fill.id}-metrics`} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                                      <td style={{ ...td, textAlign: 'right' }}>
                                        {isEditingGroup ? <input type="number" step="0.0001" value={fill.price ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'price', e.target.value)} style={{ ...inputStyle, width: '100px' }} /> : formatMoneyValue(fill.price, fill.currency)}
                                      </td>
                                      <td style={{ ...td, textAlign: 'right', color: valueColor(toBaseMoney(fill.return_amount, fill.currency, fill.fx_rate_to_base, displayBaseCurrency)) }}>
                                        {formatMoneyValue(toBaseMoney(fill.return_amount, fill.currency, fill.fx_rate_to_base, displayBaseCurrency), displayBaseCurrency)}
                                      </td>
                                      <td style={{ ...td, textAlign: 'right' }}>
                                        {isEditingGroup ? <input type="number" step="0.0001" value={fill.commission ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'commission', e.target.value)} style={{ ...inputStyle, width: '100px' }} /> : formatMoneyValue(toBaseMoney(fill.commission, fill.currency, fill.fx_rate_to_base, displayBaseCurrency), displayBaseCurrency)}
                                      </td>
                                      <td style={{ ...td, textAlign: 'right' }}>
                                        {isEditingGroup ? <input type="number" step="0.0001" value={fill.fees ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'fees', e.target.value)} style={{ ...inputStyle, width: '100px' }} /> : formatMoneyValue(toBaseMoney(fill.fees, fill.currency, fill.fx_rate_to_base, displayBaseCurrency), displayBaseCurrency)}
                                      </td>
                                      <td style={{ ...td, textAlign: 'right' }}>{isEditingGroup ? <input type="number" step="0.00001" value={fill.fx_rate_to_base ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'fx_rate_to_base', e.target.value)} style={{ ...inputStyle, width: '90px' }} /> : (fill.fx_rate_to_base != null ? Number(fill.fx_rate_to_base).toFixed(5) : '—')}</td>
                                      <td style={td}>{isEditingGroup ? <input value={fill.currency ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'currency', e.target.value)} style={{ ...inputStyle, width: '80px' }} /> : (fill.currency || '—')}</td>
                                      <td style={td}>{isEditingGroup ? <input value={fill.trade_type ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'trade_type', e.target.value)} style={{ ...inputStyle, width: '110px' }} /> : (fill.trade_type || '—')}</td>
                                      <td style={td}>
                                        {isEditingGroup ? (
                                          <input value={fill.notes ?? ''} onChange={e => updateGroupEditField(group.group_key, fill.id, 'notes', e.target.value)} style={{ ...inputStyle, width: '180px' }} />
                                        ) : (
                                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                            {fill.trade_type === 'BookTrade' && (
                                              <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '4px', background: '#ffffff10', color: COLORS.muted }}>Book</span>
                                            )}
                                            {fill.notes && fill.notes.split(',').map(n => n.trim()).filter(Boolean).map(note => (
                                              <span key={note} style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '4px', background: (NOTES_COLOR[note] || '#64748b') + '33', color: NOTES_COLOR[note] || COLORS.muted }}>
                                                {NOTES_LABEL[note] || note}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {group.commissions_base > 0 && (
                                <div style={{ textAlign: 'right', fontSize: '12px', color: COLORS.muted, marginTop: '8px' }}>
                                  Commissions gesamt: <span style={{ color: COLORS.red }}>{formatMoneyValue(-group.commissions_base, displayBaseCurrency)}</span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      <tr style={{ height: '8px' }} />
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {tradeListMode !== 'all' && displayedGroupTotal > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '12px', color: COLORS.muted }}>
              Seite {currentPageNumber} von {totalPageCount}
              {tradeJournalGroupPageLoading ? ' • lädt…' : ''}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Btn
                type="button"
                color="ghost"
                onClick={() => setTradePageOffset(offset => Math.max(0, offset - currentTradePageSize))}
                disabled={!canPageBackward || tradeJournalGroupPageLoading}
              >
                Zurück
              </Btn>
              <Btn
                type="button"
                color="ghost"
                onClick={() => setTradePageOffset(offset => offset + currentTradePageSize)}
                disabled={!canPageForward || tradeJournalGroupPageLoading}
              >
                Weiter
              </Btn>
            </div>
          </div>
        )}
      </Card>
    </section>
  );
}

function StatItem({ label, value, color, hint, COLORS }) {
  return (
    <div style={{ minWidth: '80px' }}>
      <div
        title={hint || undefined}
        style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px', cursor: hint ? 'help' : 'default' }}
      >
        {label}
      </div>
      <div style={{ fontSize: '18px', fontWeight: 700, color: color || COLORS.text }}>{value}</div>
    </div>
  );
}
